<template>
    <div
        class="map"
        :class="{ 'is-tablet-sidebar-toggled': tabletSidebarToggled, 'is-on-low-zoomlevel' : this.mapZoomLevel < TRAFFIC_MARKER_ZOOM_BREAKPOINT, 'is-on-medium-zoomlevel' : this.mapZoomLevel < TRAFFIC_MARKER_ZOOM_MEDIUM_BREAKPOINT, 'drag-route-marker-clickable' : dragRouteMarkerClickable,'drag-route-marker-visible' : dragRouteMarkerVisible, 'drag-route-marker-dragging' : dragRouteMarkerDragging, 'drag-route-marker-green' : activeTransportModeTab === 'BIKE' }"
    >
        <SVGBlurFilter v-if="!mapGlSupported" />

        <img :src="blurredMapboxImage" v-if="!mapboxServiceAccepted" class="map__placeholder-image" />

        <div ref="map-container" id="map-container" class="map__container"></div>

        <!-- poi reload button -->
        <transition name="fade">
            <SmrButton class="map__poi-reload map__screen-bottom-el" size="tiny" :round="true" custom-class="wide" v-if="poiReloadButtonVisible" @click.native="poiReloadButtonClickHandler" :loading="poiLoading" :style="{bottom: bottomElOffset}">Hier suchen</SmrButton>
        </transition>

        <!-- controls -->
        <div class="map__controls map__controls--top-left" v-if="mobile">
            <div class="map__controls__group">
                <MapInfo />
            </div>
        </div>
        <div class="map__controls map__controls--top-right">
            <div class="map__controls__group">
                <div class="map__controls__item map__controls__item--layers" @click="showMapLayerSettings" v-if="mobile">
                    <icon>
                        <iconLayers />
                    </icon>
                </div>
                <SmrTooltip content="Eigene Position anzeigen">
                    <div class="map__controls__item map__controls__item--locate" @click="getUserLocation">
                        <icon>
                            <iconLocate />
                        </icon>
                    </div>
                </SmrTooltip>    
                <SmrTooltip content="Fahrzeurg-Position anzeigen" v-if="true === false">
                    <div class="map__controls__item map__controls__item--car">
                        <icon>
                            <iconCar />
                        </icon>
                    </div>
                </SmrTooltip>
            </div>
            <SmrTooltip content="Weitere Informationen" v-if="!mobile">
                <div class="map__controls__group">
                    <MapInfo />
                </div>
            </SmrTooltip>
        </div>
        <div
            class="map__controls map__controls--bottom-right"
            v-if="!mobile"
        >
            <SmrTooltip content="Kartenstil ändern" v-if="mapGlSupported">
                <div class="map__controls__group">
                    <div class="map__controls__item" @click="showMapLayerSettings">
                        <icon>
                            <iconLayers />
                        </icon>
                    </div>
                </div>
            </SmrTooltip>    
            <SmrTooltip content="Kartenausschnitt ändern">
                <div class="map__controls__group">
                    <div class="map__controls__item" @click="zoomMapInClickHandler">
                        <icon>
                            <iconPlus />
                        </icon>
                    </div>
                    <div
                        class="map__controls__item"
                        @click="zoomMapOutClickHandler"
                    >
                        <icon>
                            <iconMinus />
                        </icon>
                    </div>
                </div>
            </SmrTooltip>
        </div>

        <MapLayerSettings v-if="mapGlSupported" ref="map-layer-settings" />
    </div>
</template>

<script>
/* global L */
import * as api from '@/api';
import eventBus from '@/utils/event-bus.js';
import { mapGetters } from 'vuex';
import { parse } from 'wkt';
import mapboxgl from '@adac-mobile/adac-maplibre-gl';
import 'mapbox.js';
import 'mapbox.js/dist/mapbox.css';
import '@adac-mobile/adac-maplibre-gl/dist/mapbox-gl.css';
import mapboxglSupported from '@mapbox/mapbox-gl-supported';
import debounce from 'lodash/debounce';
import turfArea from '@turf/area'
import turfPointToLineDistance from '@turf/point-to-line-distance';
import MapLayerSettings from '@/components/MapLayerSettings.vue';
import MapInfo from '@/components/MapInfo.vue';
import Icon from '@/components/Icon.vue';
import IconPlus from '@/components/icons/IconPlus.vue';
import IconMinus from '@/components/icons/IconMinus.vue';
import IconLocate from '@/components/icons/IconLocate.vue';
import IconCar from '@/components/icons/IconCar.vue';
import IconLayers from '@/components/icons/IconLayers.vue';
import SmrButton from '@/components/SmrButton.vue'
import SmrTooltip from '@/components/SmrTooltip.vue'
import SVGBlurFilter from '@/components/SVGBlurFilter'
import messageBoxBlueprint from '@/mixins/message-box-blueprint.js';
import { setPermanentData, getPermanentData } from '@/utils/browser-storage';
import * as markerElements from '@/utils/map-elements/markers';
import * as pinDropElements from '@/utils/map-elements/pin-drop';
import { ONLY_LOGGED_IN_AVAILABLE, getImageForCategory, isOeamtcPartner, getCategory, getCoordinatesObject, getPoiId, isFavourite, getSubApiTypes, getActiveSubApiTypes, getParentApi, getPoiType, getSubType, getPOIResultByType } from '@/utils/poi-utils'
import { getPOIPrice } from '@/utils/poi-data/prices';
import { formatPrice } from '@/utils/format'
import { getPoiParams } from '@/utils/poi-params';
import { getRouteById, getListResultRouteData } from '@/utils/route-utils'
import { updateQueryParams, decodeQueryParams } from '@/utils/url-utils'
import { toLatLngArray } from '@/utils/map-utils'
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import { loadMorePOIResultsWithPagination } from '@/utils/poi-api-utils'
import { isDotMarker, hasDotMarkerType, isZoomLevelBasedType, getAreas, getAreaDisplaySettings, getZoomLevelVisible } from '@/utils/poi-map-utils'
import { mapboxServiceAccepted } from '@/utils/user-centrics'
import { poiDetailsTracking } from '@/data/trackingInfo';

export default {
    components: {
        MapLayerSettings,
        MapInfo,
        Icon,
        IconPlus,
        IconMinus,
        IconLocate,
        IconCar,
        IconLayers,
        SmrButton,
        SmrTooltip,
        SVGBlurFilter,
    },
    mixins: [messageBoxBlueprint],
    props: {
        tabletSidebarToggled: {
            default: false,
            type: Boolean,
        },
    },
    data() {
        return {
            mapPOI: false,
            timer: false,
            dragging: false,
            touchAmount: 0,
            mapbox: false,
            map: null,
            mapLoaded: false,
            mapOptions: {
                center: [13.449884, 47.568851],
                zoom: 7,
            },
            markerLocations: {
                userLocation: {
                    exists: false,
                    coordinates: [12.2249088, 47.6928634],
                },
                singleLocation: {
                    show: false,
                    isPinDrop: false,
                    position: false,
                    coordinates: [0, 0],
                },
                startLocation: {
                    show: false,
                    coordinates: [0, 0],
                },
                endLocation: {
                    show: false,
                    coordinates: [0, 0],
                },
                dragRoute: {
                    show: false,
                    coordinates: [0, 0],
                }
            },
            markers: {},
            areas: {},
            routes: [],
            trafficLines: [],
            previousTrafficLines: [],
            pinDropPopup: false,
            routeMarkerPopup: false,
            poiReloadButtonVisible: false,
            mapOnlyZoomedIn: false,
            automaticPoiReloadTimeout: false,
            containerResizeListener: false,
            mapZoomLevel: false,
            dotMarkersActive: false,
            bottomElOffset: false,
            ROUTE_LAYER_NEIGHBOUR: 'building-3d',
            AREA_LAYER_NEIGHBOUR: 'cablecar',
            AUTOMATIC_POI_RELOAD_DELAY: 1000,
            DOT_MARKER_ZOOM_BREAKPOINT: 12,
            TRAFFIC_MARKER_ZOOM_MEDIUM_BREAKPOINT: 15,
            TRAFFIC_MARKER_ZOOM_BREAKPOINT: 13,
            AREA_POI_TYPES: ['shorttermparking', 'greenzone'],
            SKIP_PINS_ON_MAP: ['shorttermparking'],
            mapBoundingBoxDEVindex: 0,
            highlightedAreas: [],
            hoveredAreaId: null,
            highlightAreaId: null,
            previousRoutes: false,
            mapImagesLoading: [],
            singlePoiShown: false,
            mapMovedManually: false,
            activePoiAreaDisplayed: false,
            activeRouteMarker: false,
            dragRouteMarkerClickable: false,
            dragRouteMarkerVisible: false,
            dragRouteMarkerDragging: false,
            dragRouteMarkerInsertAfter: false,
            markerHovered: false,
            draggedMarker: false,
            mapboxServiceAccepted: true,
            blurredMapboxImage: `${process.env.BASE_URL}/images/mapbox-blur.jpg`,
        };
    },
    computed: {
        ...mapGetters(['mapGlSupported', 'mapAccessToken', 'mapApimKey', 'mapboxJsAccessToken', 'mapStyle', 'mobile', 'startWaypoint', 'endWaypoint', 'interimWaypoints', 'pinDrop', 'activePoiId', 'selectedPOIs', 'mapBounds', 'poiLoading', 'waypointPositions', 'poiSettings', 'user', 'allFavourites', 'routeCorridor', 'mobileSidebarHeightMode', 'mobileSidebarHeight', 'activeRouteId', 'centerFromUrl', 'previousActivePoi', 'isWaypointLimitReached', 'loadingPOIs', 'userCoordinates', 'user', 'routeWaypoints', 'activeTransportModeTab', 'activeRouteSectionPolylines', 'activeTransportModeNoIntermediateStops', 'poiResults']),
        mobileMapOffset() {
            if (this.mobile && this.mobileSidebarHeightMode !== 'minimal') {
                return (window.innerHeight - 50) / 4;
            }
            return 0;
        },
        layerStyles() {
            if(this.mapGlSupported) {
                return {
                    // primary
                    primary: {
                        type: 'line',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                        paint: {
                            'line-color': ['get', 'color'],
                            'line-width': 6,
                        },
                    },
                    primaryBg: {
                        type: 'line',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                        paint: {
                            'line-color': '#ffffff',
                            'line-width': 10,
                        },
                    },
                    primaryShadow: {
                        type: 'line',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                        paint: {
                            'line-color': '#000000',
                            'line-width': 14,
                            'line-blur': 7,
                            'line-opacity': this.mobile ? 0.4 : 0.6,
                        },
                    },
                    primaryDotted: {
                        type: 'symbol',
                        layout: {
                            'icon-image': [
                                'match',
                                ['get', 'color'],
                                '#42ab09',
                                'routeDotGreen',
                                'routeDotDefault'
                            ],
                            'icon-size': 1,
                            'symbol-placement': 'line',
                            'symbol-spacing': 1,
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                            'icon-padding': 0,
                        },
                    },
                    // secondary
                    secondary: {
                        type: 'line',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                        paint: {
                            'line-color': '#d6d6d6',
                            'line-width': this.mobile ? 4 : 6,
                        },
                    },
                    secondaryBg: {
                        type: 'line',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                        paint: {
                            'line-color': '#adadad',
                            'line-width': this.mobile ? 7 : 10,
                        },
                    },
                    secondaryDotted: {
                        type: 'symbol',
                        layout: {
                            'icon-image': 'routeDotSecondary',
                            'icon-size': 1,
                            'symbol-placement': 'line',
                            'symbol-spacing': 1,
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                            'icon-padding': 0,
                        },
                    },
                    // traffic
                    traffic: {
                        type: 'line',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                        paint: {
                            'line-color': '#4F4D56',
                            'line-width': 4,
                        },
                    },
                }
            } else {
                return {
                    // primary
                    primary: {
                        lineJoin: 'round',
                        lineCap: 'round',
                        weight: 5,
                    },
                    primaryBg: {
                        lineJoin: 'round',
                        lineCap: 'round',
                        color: '#ffffff',
                        weight: this.mobile ? 9 : 11,
                    },
                    primaryShadow: {
                        lineJoin: 'round',
                        lineCap: 'round',
                        color: '#000000',
                        weight: this.mobile ? 8 : 10,
                        opacity: this.mobile ? 0.4 : 0.6,
                        className: 'blurred-path',
                    },
                    // secondary
                    secondary: {
                        lineJoin: 'round',
                        lineCap: 'round',
                        weight: this.mobile ? 4 : 5,
                        color: '#d6d6d6',
                    },
                    secondaryBg: {
                        lineJoin: 'round',
                        lineCap: 'round',
                        color: '#b8b8b8',
                        weight: this.mobile ? 7 : 8,
                        className: 'secondary-route-bg'
                    },
                    // traffic
                    traffic: {
                        lineJoin: 'round',
                        lineCap: 'round',
                        weight: 4,
                        color: '#4F4D56',
                        className: 'traffic-line'
                    }
                }
            }
        },
    },
    watch: {
        mapStyle() {
            this.updateStyle();
        },
        selectedPOIs(val) {
            if(!val.length) {
                this.poiReloadButtonVisible = false;
                this.$store.commit('setMapMoved', false)
            }
            this.storeZoom();
        },
        poiLoading(val) {
            if(!val) {
                this.poiReloadButtonVisible = false;
                this.$store.commit('setMapMoved', false)
            }
        },
        mobileSidebarHeightMode(val, oldVal) {
            if(this.mobile && val !== 'full' && oldVal !== 'full') {
                this.updateBottomElPlacement();
            }
        },
        activePoiId(activePoiId, oldActivePoiId) {
            this.toggleActivePoi(oldActivePoiId, false);
            this.toggleActivePoi(activePoiId);
            if(!oldActivePoiId) {
                this.toggleSingleLocationActivity(false);
            } else if (!activePoiId) {
                this.toggleSingleLocationActivity(true);
            }
        },
        mapZoomLevel(newVal, oldVal) {
            if(oldVal === false) {
                this.dotMarkersActive = newVal < this.DOT_MARKER_ZOOM_BREAKPOINT;
            } else if (oldVal < this.DOT_MARKER_ZOOM_BREAKPOINT && newVal >= this.DOT_MARKER_ZOOM_BREAKPOINT) {
                this.dotMarkersActive = false;
                this.toggleDotPoiMarkers()
            } else if (oldVal >= this.DOT_MARKER_ZOOM_BREAKPOINT && newVal < this.DOT_MARKER_ZOOM_BREAKPOINT) {
                this.dotMarkersActive = true;
                this.toggleDotPoiMarkers()
            }
            this.toggleZoomLevelBasedMarkers()
        },
        user() {
            this.updateLockedMarkers()
        }
    },
    mounted() {
        this.$store.commit('setMapGlSupported', mapboxglSupported.supported())
        setTimeout(() => {
            this.getStoredMapStyle();
            this.initMap();
        }, 10);
    },
    beforeDestroy() {
        this.removeListeners();
    },
    methods: {
        getStoredMapStyle() {
            const storedMapStyle = getPermanentData('map-style');
            if(storedMapStyle) {
                this.$store.commit('setActiveMapStyle', storedMapStyle.style);
            }
        },
        initMap() {
            if(this.mapGlSupported) {
                this.mapbox = mapboxgl;
                mapboxgl.apimKey = this.mapApimKey
                this.map = new mapboxgl.Map({
                // this.map = new Mapbox.Map({
                    container: this.$refs['map-container'],
                    style: this.mapStyle,
                    center: this.mapOptions.center,
                    zoom: this.mapOptions.zoom,
                    dragRotate: false,
                    renderWorldCopies: false,
                    maxBounds: [
                        [-180, -85],
                        [180, 85]
                    ],
                });
                const scale = new mapboxgl.ScaleControl({
                // const scale = new Mapbox.ScaleControl({
                    maxWidth: 100,
                });
                this.map.addControl(scale, 'top-left');
                this.map.touchZoomRotate.disableRotation();
                this.map.on('load', this.onMapLoaded);
            } else {
                this.mapboxServiceAccepted = mapboxServiceAccepted()
                if(this.mapboxServiceAccepted) {
                    eventBus.$off('initMap', this.reInitMapHandler)

                    this.mapbox = L.mapbox
                    L.mapbox.accessToken = this.mapboxJsAccessToken;
                    this.map = L.mapbox.map('map-container', 'mapboxjs-map', {
                        zoomControl: false
                    }).setView(toLatLngArray(this.mapOptions.center), this.mapOptions.zoom)
                    L.mapbox.styleLayer('mapbox://styles/oeamtcgeo1/cjdzrhx813yla2rqn53x5m7y6').addTo(this.map);
                    this.map.whenReady(() => {
                        setTimeout(() => {
                            if(!this.mapLoaded) this.onMapLoaded()
                        }, 10);
                    })
                } else {
                    eventBus.$on('initMap', this.reInitMapHandler)
                    setTimeout(() => {
                        eventBus.$emit('showMapboxServiceInfo')
                    }, 1000);
                }
            }
        },
        reInitMapHandler() {
            this.initMap()
        },
        async onMapLoaded() {
            this.mapLoaded = true;
            this.addMarker('dragRoute', [0, 0])
            this.addListeners();
            this.checkForUserLocation();
            this.loadMapImages();
            this.storeMapBounds();
            this.$store.commit('setMapLoaded', true)
        },
        loadMapImageById(id) {
            if(!this.mapGlSupported) return
            const imagePaths = {
                routeDotDefault: `${process.env.BASE_URL}/images/map-elements/route-dot-default.png`,
                routeDotGreen: `${process.env.BASE_URL}/images/map-elements/route-dot-green.png`,
                routeDotSecondary: `${process.env.BASE_URL}/images/map-elements/route-dot-secondary.png`,
            }
            if(!this.map.hasImage(id) && !this.mapImagesLoading.includes(id)) {
                this.mapImagesLoading.push(id)
                this.map.loadImage(
                    imagePaths[id],
                    (error, image) => {
                        if (error) throw error;
                        this.map.addImage(id, image);
                        this.mapImagesLoading = this.mapImagesLoading.filter(images => images !== id)
                    },
                );
            } 
        },
        loadMapImages() {
            this.loadMapImageById('routeDotDefault')
            this.loadMapImageById('routeDotGreen')
            this.loadMapImageById('routeDotSecondary')
        },
        addListeners() {
            eventBus.$on('poiDetails', this.poiDetailsHandler);
            eventBus.$on('showSinglePoint', this.showSinglePointHandler);
            eventBus.$on('removeSinglePoint', this.removeSinglePointHandler);
            eventBus.$on('showRouteOnMap', this.showRouteOnMapHandler);
            eventBus.$on('removeRoutesFromMap',this.removeRoutesFromMapHandler);
            eventBus.$on('toggleHighlightPoi', this.toggleHighlightPoiHandler);
            eventBus.$on('removePinDrop', this.removePinDropHandler)
            eventBus.$on('removePoisFromMap', this.removePoisFromMapHandler)
            eventBus.$on('showPoisOnMap', this.showPoisOnMapHandler)
            eventBus.$on('favouriteAdded', this.favouriteAddedHandler)
            eventBus.$on('toggleFavourite', this.toggleFavouriteMarker)
            eventBus.$on('updateMapMarker', this.updateMapMarkerHandler)
            eventBus.$on('showSinglePoiOnMap', this.showSinglePoiOnMapHandler)
            eventBus.$on('hideSinglePoiOnMap', this.hideSinglePoiOnMapHandler)
            eventBus.$on('urlQueryChanged', this.urlQueryChangedHandler)
            eventBus.$on('showPoiAreaOnMap', this.showPoiAreaOnMapHandler)
            eventBus.$on('zoomToCoordinates', this.zoomToCoordinatesHandler)
            // eventBus.$on('drawBoundingBox', this.drawBoundingBoxHandler) // dev
            this.map.on('click', this.mapClickHandler);
            this.map.on('contextmenu', this.mapContextMenuHandler);
            this.map.on('moveend', this.mapMoveHandler)
            this.map.on('mousedown', this.mapMouseDownHandler)
            this.map.on('mousemove', this.mapMouseMoveHandler)
            if(this.mapGlSupported) {
                this.map.on('touchstart', this.mapTouchStartHandler);
                this.map.on('touchend', this.mapTouchEndHandler);
                this.map.on('touchmove', this.mapTouchMoveHandler);
                this.map.on('zoom', this.mapZoomHandler)
                this.map.on('styledata', this.mapStyleDataHandler)
                this.map.on('styleimagemissing', this.mapStyleImageMissingHandler)
            } else {
                this.map.on('zoomend', this.mapZoomHandler)
            }
            document.addEventListener('click', this.documentClickHandler)
            this.addContainerResizeListener();
        },
        removeListeners() {
            eventBus.$off('poiDetails', this.poiDetailsHandler);
            eventBus.$off('showSinglePoint', this.showSinglePointHandler);
            eventBus.$off('removeSinglePoint', this.removeSinglePointHandler);
            eventBus.$off('showRouteOnMap', this.showRouteOnMapHandler);
            eventBus.$off('removeRoutesFromMap', this.removeRoutesFromMapHandler);
            eventBus.$off('toggleHighlightPoi', this.toggleHighlightPoiHandler);
            eventBus.$off('removePinDrop', this.removePinDropHandler)
            eventBus.$off('removePoisFromMap', this.removePoisFromMapHandler)
            eventBus.$off('showPoisOnMap', this.showPoisOnMapHandler)
            eventBus.$off('favouriteAdded', this.favouriteAddedHandler)
            eventBus.$off('toggleFavourite', this.toggleFavouriteMarker)
            eventBus.$off('updateMapMarker', this.updateMapMarkerHandler)
            eventBus.$off('showSinglePoiOnMap', this.showSinglePoiOnMapHandler)
            eventBus.$off('hideSinglePoiOnMap', this.hideSinglePoiOnMapHandler)
            this.map.off('click', this.mapClickHandler);
            this.map.off('contextmenu', this.mapContextMenuHandler);
            this.map.off('moveend', this.mapMoveHandler)
            this.map.off('mousedown', this.mapMouseDownHandler)
            this.map.off('mousemove', this.mapMouseMoveHandler)
            eventBus.$off('zoomToCoordinates', this.zoomToCoordinatesHandler)
            if(this.mapGlSupported) {
                this.map.off('touchstart', this.mapTouchStartHandler);
                this.map.off('touchend', this.mapTouchEndHandler);
                this.map.off('touchmove', this.mapTouchMoveHandler);
                this.map.off('zoom', this.mapZoomHandler)
                this.map.off('styledata', this.mapStyleDataHandler)
                this.map.off('styleimagemissing', this.mapStyleImageMissingHandler)
            } else {
                this.map.off('zoomend', this.mapZoomHandler)
            }
            document.removeEventListener('click', this.documentClickHandler)
            this.removeContainerResizeListener();
        },
        poiDetailsHandler(data) {
            if(this.mapPOI) {
                trackEvent({ category: data.trackingCategory, action: data.trackingAction, label: data.trackingLabel });
                trackGa4Event({
                    ga4Parameter1Name: poiDetailsTracking(data.ga4EventName, data.trackingLabel, data.ga4Parameter1Value).ga4Parameter1Name,
                    ga4Parameter1Value: poiDetailsTracking(data.ga4EventName, data.trackingLabel, data.ga4Parameter1Value).ga4Parameter1Value, 
                    ga4Event: poiDetailsTracking(data.ga4EventName, data.trackingLabel, data.ga4Parameter1Value).ga4Event}
                )
            }
            this.mapPOI = false;
        },
        // event handlers
        zoomMapInClickHandler() {
            this.map.zoomIn();
        },
        zoomMapOutClickHandler() {
            this.map.zoomOut();
        },
        showSinglePointHandler(data) {
            this.showSinglePointOnMap(data.waypoint, data.jumpToPoint);
        },
        removeSinglePointHandler() {
            this.removeSinglePointFromMap();
        },
        showRouteOnMapHandler(route) {
            if(route.type === 'secondary') {
                if(event && event.type === 'click') {
                    trackGa4Event({ga4Parameter1Name: 'route', ga4Parameter1Value: 'select_alternative_routes', ga4Event: 'route_planner'})
                }
            }
            this.showRouteOnMap(route);
        },
        removeRoutesFromMapHandler(routeToSkip) {
            this.removeRoutesFromMap(routeToSkip);
        },
        mapClickHandler(e) {
            this.checkClickedFeatures(e)
        },
        mapContextMenuHandler(e) {
            if(this.mobile) return
            const coordinates = e.lngLat ? e.lngLat : e.latlng
            this.togglePinDropMarker(coordinates)
        },
        mapMouseDownHandler(e) {
            this.checkMouseDownFeatures(e)
        },
        mapMouseMoveHandler(e) {
            if(!this.mapGlSupported || this.mobile) return
            const routeBbox = [[e.point.x - 5, e.point.y - 5], [e.point.x + 5, e.point.y + 5]];
            const allRoutes = [...this.routes, ...this.routes.map(r => r + '-dotted')]
            const routeFeatures = this.map.queryRenderedFeatures(routeBbox, { layers: allRoutes });
            if(routeFeatures.length > 0 && !this.markerHovered && !this.draggedMarker && !this.activeTransportModeNoIntermediateStops) {
                const hoveredRouteId = routeFeatures[0].layer ? routeFeatures[0].layer.id.replace('-dotted', '') : false
                if(hoveredRouteId && hoveredRouteId === this.activeRouteId) {
                    // follow the mouse
                    if(this.markers.dragRoute) {
                        this.markers.dragRoute.setLngLat(e.lngLat)
                    }
                    this.dragRouteMarkerClickable = true
                    this.dragRouteMarkerVisible = true
                    this.markers.dragRoute.setDraggable(true)
                }
            } else if(this.draggedMarker && this.draggedMarker.type === 'dragRoute') {
                this.dragRouteMarkerClickable = true
                // this.dragRouteMarkerVisible = true
            } else {
                this.dragRouteMarkerClickable = false
                this.dragRouteMarkerVisible = false
                this.markers.dragRoute.setDraggable(false)
            }        
        },
        mapTouchStartHandler(e) {
            if(!this.mobile) return
            const coordinates = e.lngLat ? e.lngLat : e.latlng
            this.dragging = false
            this.touchAmount = e.originalEvent.touches.length
            this.timer = setTimeout(() => {
                if(!this.dragging && this.touchAmount === 1) {
                    if(!this.markerLocations.singleLocation.isPinDrop) {
                        this.togglePinDropMarker(coordinates)
                    } else {
                        // on mobile. if pindrop already exist, delete the old one and add the new one
                        this.removePinDropMarker()
                        this.togglePinDropMarker(coordinates)
                    }
                }
            }, 500); 
        },
        mapTouchEndHandler() {
            this.dragging = false
            this.touchAmount = 0
            if(this.timer) clearTimeout(this.timer)
        },
        mapTouchMoveHandler() {
            this.dragging = true
        },
        mapMoveHandler() {
            this.mapMovedManually = true
            if(this.selectedPOIs.length) {
                if(this.isZoomIn() && !this.poiReloadButtonVisible) {
                    // this.reloadPoisAfterDelay();
                } else {
                    this.mapOnlyZoomedIn = false;
                }

                this.showPoiReloadButton();
                this.$store.commit('setMapMoved', true)
            }
            this.storeMapBounds();
            this.updateUrlQuery()
            
            setTimeout(() => {
                this.mapMovedManually = false
            }, 1000);
        },
        mapZoomHandler() {
            this.storeZoom();
            this.checkToLoadAdditionalPOIs()
        },
        documentClickHandler(e) {
            let target = e.target
            if(target.classList.contains('js-pin-drop-button') || target.closest('.js-pin-drop-button')) {
                target = !target.classList.contains('js-pin-drop-button') ? target.closest('.js-pin-drop-button') : target
                if(target.dataset.action) { // its a route marker
                    const action = target.dataset.action
                    const waypointIndex = target.dataset.waypointIndex
                    eventBus.$emit('editWaypointsWithAction', { action, waypointIndex })
                } else {
                    const data = this.pinDrop.data
                    this.togglePinDropMarker()
                    eventBus.$emit('addLocationToWaypoints', { 
                        waypoint: data,
                        position: target.dataset.position,
                        positionIndex: false,
                        insertAfterIndex: false,
                        disableLocationJump: true,
                    })
                }
                if(target.dataset.trackingLabel) {
                    trackEvent({ category: 'Route Planner', action: 'Pindrop', label: target.dataset.trackingLabel })
                }
                if(target.dataset.ga4TrackingLabel) {
                    let ga4TrackingLabel = target.dataset.ga4TrackingLabel === 'stop' ? 'middle_point' : target.dataset.ga4TrackingLabel; 
                    trackGa4Event({ga4Parameter1Name: ga4TrackingLabel, ga4Parameter1Value: 'pindrop', ga4Event: 'route_planner'})
                }
            }
            if(target.classList.contains('js-pin-drop-close') || target.closest('.js-pin-drop-close')) {
                const closeButton = target.classList.contains('js-pin-drop-close') ? target : target.closest('.js-pin-drop-close')
                const closeButtonType = closeButton.dataset.type
                if(closeButtonType === 'routepopup') {
                    this.deactivateRouteMarker(this.activeRouteMarker)
                } else {
                    this.togglePinDropMarker() 
                    trackEvent({ category: 'Route Planner', action: 'Pindrop', label: 'Close' })
                }
            }
            if(target.classList.contains('js-pin-drop-favourite') || target.closest('.js-pin-drop-favourite')) {
                target = !target.classList.contains('js-pin-drop-favourite') ? target.closest('.js-pin-drop-favourite') : target
                pinDropElements.togglePinDropFavourite(target)
                trackEvent({ category: 'Route Planner', action: 'Pindrop', label: 'Save' })
            }
        },
        singleLocationClickHandler(e) {
            e.stopPropagation();
            this.emitDeactivatePoi();
        },
        routeMarkerClickHandler(e) {
            let target 
            if(this.mapGlSupported) {
                e.stopPropagation();
                target = e.currentTarget
            } else {
                target = e.target
            }
            this.activateRouteMarker(target.type, target.interimIndex)
        },
        poiClickHandler(e) {
            if(this.singlePoiShown) return // do not allow closing single POIs when clicking on markers
            
            if(this.previousActivePoi && !this.previousActivePoi.showingMapPins) {
                this.$store.commit('setPreviousActivePoi', false)
            }
            let target 
            if(this.mapGlSupported) {
                e.stopPropagation();
                target = e.currentTarget
            } else {
                target = e.target
            }
            if(target.locked && !this.user) {
                this.$store.commit('setOpenPOIAfterLogin', {
                    type: target.poiType,
                    id: target.poiId,
                    subType: target.subType
                })
                eventBus.$emit('showLoginMenu')
                return
            }
            this.activatePoi(target.poiId, target.poiType, target.subType)
        },
        poiMouseEnterHandler(e) {
            let target 
            if(this.mapGlSupported) {
                e.stopPropagation();
                target = e.currentTarget
            } else {
                target = e.target
            }
            if(target.highlight === 'true') return false;
            this.emitToggleHighlightPoi(target.poiId, true);
        },
        poiMouseLeaveHandler(e) {
            let target 
            if(this.mapGlSupported) {
                e.stopPropagation();
                target = e.currentTarget
            } else {
                target = e.target
            }
            this.emitToggleHighlightPoi(target.poiId, false);
        },

        toggleHighlightPoiHandler(data) {
            this.toggleHighlightPoi(data);
        },
        
        poiReloadButtonClickHandler() {
            this.reloadPois();
        },
        removePoisFromMapHandler(payload) {
            this.removePoiResults(payload.type, payload.isSubApi);
        },
        showPoisOnMapHandler(payload) {
            this.showPoiResults(payload.data, payload.type, payload.isSubApi);
        },

        showMapLayerSettings() {
            this.$refs['map-layer-settings'].showDialog();
        },

        addContainerResizeListener() {
            this.containerResizeListener = new ResizeObserver(() => {
                this.resizeMap();
            });
            this.containerResizeListener.observe(this.$refs['map-container']);
        },

        removeContainerResizeListener() {
            this.containerResizeListener.unobserve(this.$refs['map-container']);
        },
        toggleFavouriteMarker(objectId) {
            if(!this.markers.poi) return
            let markerToUpdate = this.markers.poi.find(marker => marker.id === objectId)
            if(!markerToUpdate) {
                // if favourite POI is active, reload the map to display the new favourite
                if(this.selectedPOIs.includes('favourites')) {
                    this.$store.dispatch('loadPOIResultsByType', { type: 'favourites' });
                }
                return
            }
            if (this.mapGlSupported) {
                markerToUpdate = markerToUpdate.getElement()
            } else {
                markerToUpdate = markerToUpdate._icon.firstChild
            }

            const hasStar = markerToUpdate.classList.contains('marker--poi--star')
            const hasDiscount = markerToUpdate.classList.contains('marker--poi--discount')
            const hasDiscountStar = markerToUpdate.classList.contains('marker--poi--discount-star')
            const isPindrop = markerToUpdate.classList.contains('marker--poi--category--location')

            if (hasStar) {
                markerToUpdate.classList.remove('marker--poi--star')
            } else if (hasDiscountStar) {
                markerToUpdate.classList.remove('marker--poi--discount-star')
                markerToUpdate.classList.add('marker--poi--discount')
            } else if (hasDiscount) {
                markerToUpdate.classList.remove('marker--poi--discount')
                markerToUpdate.classList.add('marker--poi--discount-star')
            } else if (!hasStar && !hasDiscount && !isPindrop) {
                markerToUpdate.classList.add('marker--poi--badge')
                markerToUpdate.classList.add('marker--poi--star')
            }
        },
        favouriteAddedHandler(favouriteObjectId) {
            const pinDropFavouriteEl = document.querySelector('.js-pin-drop-favourite')
            if(pinDropFavouriteEl) {
                const favouriteData = pinDropFavouriteEl.dataset.favouriteLocation ? JSON.parse(JSON.parse(pinDropFavouriteEl.dataset.favouriteLocation)) : false
                if(favouriteData && (favouriteData.locationId === favouriteObjectId || favouriteData.id === favouriteObjectId)) pinDropFavouriteEl.classList.add('is-active')
            }
        },
        mapLayerMouseEnterHandler() {
            this.updateMapCursor('pointer')
        },
        mapLayerMouseLeaveHandler() {
            this.updateMapCursor('inherit')
        },
        mapAreaMouseMoveHandler(e) {
            const poiId = e.features[0].id
            if(this.hoveredAreaId !== null && this.hoveredAreaId !== poiId) {
                this.emitToggleHighlightPoi(this.hoveredAreaId, false);
            }
            this.hoveredAreaId = poiId
            this.mapLayerMouseEnterHandler()
            this.emitToggleHighlightPoi(poiId, true);
        },
        mapAreaMouseLeaveHandler() {
            this.mapLayerMouseLeaveHandler()
            if(this.hoveredAreaId !== null) {
                this.emitToggleHighlightPoi(this.hoveredAreaId, false);
            }
        },
        updateMapMarkerHandler(e) {
            const markerToUpdate = this.markers.poi.find(marker => marker.id === e.id)
            if(markerToUpdate) {
                markerToUpdate.setLngLat([e.lon, e.lat])
            }
        },
        mapStyleDataHandler() {
            if(this.previousRoutes && this.previousRoutes.length > 0) {
                this.removeRoutesFromMap();
                const i = setInterval(() => {
                    if(this.map.isStyleLoaded()) {
                        this.showPreviousRoutes()
                        clearInterval(i)
                    }
                }, 200);
            }
            if(this.previousTrafficLines && this.previousTrafficLines.length > 0) {
                this.removeTrafficLinesFromMap()
                const i = setInterval(() => {
                    if(this.map.isStyleLoaded()) {
                        this.showPreviousTrafficLines()
                        clearInterval(i)
                    }
                }, 200);
            }
        },
        mapStyleImageMissingHandler(e) {
            this.loadMapImageById(e.id)
        },
        showSinglePoiOnMapHandler(poi) {
            this.singlePoiShown = true
            let poiType = poi.poiType
            if(poi.subType && this.AREA_POI_TYPES.includes(poi.subType)) {
                poiType = poi.subType
            }
            this.showPoiResults([poi], poiType, poi.subType)
            const coordinates = getCoordinatesObject(poi.poiType, poi)
            this.jumpToPoi([coordinates.longitude, coordinates.latitude])
        },
        hideSinglePoiOnMapHandler(poi) {
            if(this.singlePoiShown) {
                let poiType = poi.poiType
                this.singlePoiShown = false
                this.removeMarker('poi', poiType);
                this.removeAreasOfType(poiType);
                if(poi.subType) {
                   this.removeMarker('poi', poi.subType);
                    this.removeAreasOfType(poi.subType);
                }
            }
        },
        urlQueryChangedHandler(e) {
            if(!this.mapMovedManually && this.urlQueryCenterDifferent(e.to)) {
                const queryData = decodeQueryParams(e.to.query)
                if(queryData.center && queryData.zoom) {
                    this.jumpToCenter(queryData.center, queryData.zoom)
                }
            }
        },
        showPoiAreaOnMapHandler(poi) {
            const id = getPoiId(poi.poiType, poi)
            this.activePoiAreaDisplayed = poi.poiType
            this.drawAreas([ poi ], poi.poiType, poi.subType)
            this.highlightArea(poi.poiType, id, 'active')
        },
        zoomToCoordinatesHandler(coordinates) {
            this.jumpToCenter(coordinates, 16, true)
        },
        markerDragStartHandler(marker) {
            const markerElement = marker.target.getElement();
            if(markerElement.type === 'dragRoute') this.dragRouteMarkerDragging = true
            this.draggedMarker = {
                type: markerElement.type,
            }
        },
        markerDragEndHandler(marker) {
            this.draggedMarker = false
            const markerElement = marker.target.getElement();
            const lngLat = marker.target.getLngLat();
            this.dragRouteMarkerClickable = false
            this.dragRouteMarkerVisible = false
            this.dragRouteMarkerDragging = false
            this.updateRouteWithDrag(lngLat, markerElement.position, markerElement.positionIndex, this.dragRouteMarkerInsertAfter)
            this.dragRouteMarkerInsertAfter = false
        },
        removePinDropHandler(data) {
            if(data.type === 'all') {
                this.removeRouteMarkerPopup()
                this.removePinDropMarker()
                this.removeMarker('startLocation');
                this.removeMarker('endLocation');
                this.removeMarker('interimLocation');
            } else if(data.type === 'routemarker') {
                this.deactivateRouteMarker(this.activeRouteMarker)
            } else {
                this.togglePinDropMarker()
            }
        },
        draggableMarkerMouseEnterHandler() {
            this.dragRouteMarkerClickable = false
            this.dragRouteMarkerVisible = false
            this.markers.dragRoute.setDraggable(false)
            this.markerHovered = true 
        },
        draggableMarkerMouseLeaveHandler() {
            this.markerHovered = false
        },
        // drawBoundingBoxHandler(boundingBox) { // dev
        //     this.mapBoundingBoxDEVindex++;
        //     this.map.addSource(`boundingBox${this.mapBoundingBoxDEVindex}`, {
        //         'type': 'geojson',
        //         'data': {
        //             'type': 'Feature',
        //             'geometry': {
        //                 'type': 'Polygon',
        //                 'coordinates': [
        //                     [
        //                         [boundingBox.longitudeMin, boundingBox.latitudeMax],
        //                         [boundingBox.longitudeMax, boundingBox.latitudeMax],
        //                         [boundingBox.longitudeMax, boundingBox.latitudeMin],
        //                         [boundingBox.longitudeMin, boundingBox.latitudeMin]
        //                     ]
        //                 ]
        //             }
        //         }
        //     });
                
        //     // Add a new layer to visualize the polygon.
        //     this.map.addLayer({
        //         'id': `boundingBox${this.mapBoundingBoxDEVindex}`,
        //         'type': 'fill',
        //         'source': `boundingBox${this.mapBoundingBoxDEVindex}`, // reference the data source
        //         'layout': {},
        //         'paint': {
        //             'fill-color': '#0080ff', // blue color fill
        //             'fill-opacity': 0.2
        //             }
        //     });
        // },

        // general
        jumpToCenter(coordinates, zoom = false, animate = false) {
            if(this.mapGlSupported) {
                this.map.flyTo({
                    center: coordinates,
                    offset: [0, -this.mobileMapOffset],
                    zoom: zoom ? zoom : 12,
                    animate: animate,
                });
            } else {
                this.map.setView(toLatLngArray(coordinates), zoom ? zoom : 12, {
                    animate: animate,
                })
            }
        },
        jumpToPoi(coordinates) {
            this.mapMovedManually = true
            if(this.mapGlSupported) {
                this.map.flyTo({
                    center: coordinates,
                    offset: [0, -this.mobileMapOffset],
                    zoom: this.DOT_MARKER_ZOOM_BREAKPOINT + 1,
                    animate: true,
                });
            } else {
                this.map.setView(toLatLngArray(coordinates), this.DOT_MARKER_ZOOM_BREAKPOINT + 1, {
                    zoomAnimation: true,
                })
            }

            setTimeout(() => {
                this.mapMovedManually = false
            }, 3000);
        },
        // markers
        addMarker(type, coordinates = false, poiOptions = {}, markerOptions = {}) {
            let icon = document.createElement('div');
            let dotEnabledEndpoint = false;
            let isDraggable = false
            let isClickable = false
            if (type === 'userLocation') {
                coordinates = this.markerLocations.userLocation.coordinates;
                icon.className = 'marker marker--user-location';
            } else if (type === 'singleLocation') {
                coordinates = this.markerLocations.singleLocation.coordinates;
                icon.className = `marker marker--poi  marker--poi--singleLocation is-marker-active`;
                let iconInner = `<div class="marker__poi-inner">
                    <div class="marker__poi-inner__marker">${markerElements.poiMarkerBig}</div>
                    <div class="marker__poi-inner__symbol">${markerElements.singleLocationInnerPin}</div>
                </div>`;
                icon.innerHTML = iconInner;
                icon.addEventListener('click', this.singleLocationClickHandler, false);
            } else if (type === 'startLocation') {
                coordinates = this.markerLocations.startLocation.coordinates;
                icon.className = 'marker marker--start-location';
                let iconInner = `<div class="marker__default-inner"></div>`;
                icon.innerHTML = iconInner;
                icon.position = 'start'
                isDraggable = true
                isClickable = true
                icon.type = type
            } else if (type === 'endLocation') {
                coordinates = this.markerLocations.endLocation.coordinates;
                icon.className = 'marker marker--end-location';
                let iconInner = `<div class="marker__default-inner"></div>`;
                icon.innerHTML = iconInner;
                icon.position = 'end'
                isDraggable = true
                isClickable = true
                icon.type = type
            } else if (type === 'interimLocation') {
                icon.className = 'marker marker--interim-location';
                if(this.activeTransportModeTab === 'BIKE') { icon.className += ' marker--transport-bike' }
                if(markerOptions.markerIndex) {
                    if(markerOptions.markerIndex >= 10) icon.className += ' marker marker--interim-location--wide'
                    let iconInner = `<div class="marker__default-inner">${markerOptions.markerIndex}</div>`;
                    icon.innerHTML = iconInner;
                }
                icon.position = 'between'
                icon.positionIndex = markerOptions.markerIndex
                isClickable = true
                isDraggable = true
                icon.type = type
                icon.interimIndex = markerOptions.markerIndex
            } else if (type === 'transportChange') {
                icon.className = 'marker marker--transport-change';
            } else if(type === 'dragRoute') {
                icon.className = 'marker marker--drag-route-stop';
                icon.innerHTML = `<div class="marker__tooltip copy-light-small">Klicken und ziehen zum Ändern</div>`
                icon.position = 'between'
                icon.type = type
                // isDraggable = true
            } else if (type === 'poi') {
                icon.className = `marker marker--poi  marker--poi--${poiOptions.secondaryType} marker--poi--category--${poiOptions.category}`;
                if(poiOptions.discount && poiOptions.star) {
                    icon.classList.add('marker--poi--badge');
                    icon.classList.add('marker--poi--discount-star');
                } else if(poiOptions.locked && poiOptions.discount) { // star and lock will never appear at the same time since the lock is only for logged out, and star only for logged in users
                    icon.classList.add('marker--poi--badge');
                    icon.classList.add('marker--poi--discount-lock');
                } else if(poiOptions.locked) {
                    icon.classList.add('marker--poi--badge');
                    icon.classList.add('marker--poi--lock');
                }else if(poiOptions.discount) {
                    icon.classList.add('marker--poi--badge');
                    icon.classList.add('marker--poi--discount');
                } else if(poiOptions.star) {
                    icon.classList.add('marker--poi--badge');
                    icon.classList.add('marker--poi--star');
                }
                if(poiOptions.text) {
                    icon.classList.add('marker--poi--text');
                }
                if(poiOptions.hiddenOnLowerZoomlevel) {
                    icon.classList.add('marker--poi--hide-lower-zoom')
                }
                if(poiOptions.hiddenOnMediumZoomlevel) {
                    icon.classList.add('marker--poi--hide-medium-zoom')
                }
                if(poiOptions.visibleOnZoomLevel && this.mapZoomLevel < poiOptions.visibleOnZoomLevel) {
                    icon.classList.add('is-marker-hidden')
                }
                icon.innerHTML = this.generatePoiMarkerContent(poiOptions.secondaryType, poiOptions.symbolUrl, poiOptions.text, poiOptions.category, poiOptions.isFavouritePoi);

                dotEnabledEndpoint = isDotMarker(poiOptions.secondaryType, poiOptions.isFavouritePoi, poiOptions.text)
            }

            if(isClickable) {
                if(this.mapGlSupported) {
                    icon.addEventListener('click', this.routeMarkerClickHandler, false)
                }
            } else {
                if(this.mapGlSupported) {
                    icon.addEventListener('click', this.poiClickHandler, false);
                    icon.addEventListener('mouseenter', this.poiMouseEnterHandler, false);
                    icon.addEventListener('mouseleave', this.poiMouseLeaveHandler, false);
                }
            }

            // Add z-index based on marker coordinate so the lower one always shows up on top of the higher one
            icon.style.zIndex = 100000 - parseInt(coordinates[1] * 1000);
   
            let addedMarker
            // Special for four predefined cases (userLocation, singleLocation, startLocation, endLocation) - needed for pinDrop marker switching
            if(this.markerLocations[type]) {
                this.markerLocations[type].show = true
                if (this.markers[type]) {
                    if(this.mapGlSupported) {
                        this.markers[type].setLngLat(coordinates);
                    } else {
                        const reversedCoordinates = toLatLngArray(coordinates)
                        this.markers[type].setLatLng(reversedCoordinates)
                        addedMarker = this.markers[type]
                    }
                } else {
                    let marker 
                    if(this.mapGlSupported) {
                        marker = new mapboxgl.Marker({
                            element: icon,
                            draggable: !this.mobile ? isDraggable : false
                        })
                        .setLngLat(coordinates)
                        if(isDraggable) {
                            icon.addEventListener('mouseenter', this.draggableMarkerMouseEnterHandler);
                            icon.addEventListener('mouseleave', this.draggableMarkerMouseLeaveHandler);
                        }
                    } else {
                        const markerIcon = L.divIcon({ html: icon.outerHTML, className: 'marker-wrapper', iconSize: L.point(1, 1) });
                        const reversedCoordinates = toLatLngArray(coordinates)
                        marker = L.marker(reversedCoordinates, {
                            icon: markerIcon,
                        }).setLatLng(reversedCoordinates)
                        addedMarker = marker
                    }
                    this.markers[type] = marker.addTo(this.map);
                    marker.on('dragstart', this.markerDragStartHandler)
                    marker.on('dragend', this.markerDragEndHandler)
                }
            } else { // That's for all standard markers
                if(!this.markers[type]) {
                    this.markers[type] = [];
                }
                let mapboxMarker
                
                if(this.mapGlSupported) {
                    mapboxMarker = new mapboxgl.Marker({
                            element: icon,
                            draggable: !this.mobile ? isDraggable : false
                        })
                        .setLngLat(coordinates)
                        .addTo(this.map);
                        if(isDraggable) {
                            icon.addEventListener('mouseenter', this.draggableMarkerMouseEnterHandler);
                            icon.addEventListener('mouseleave', this.draggableMarkerMouseLeaveHandler);
                        }
                } else {
                    const markerIcon = L.divIcon({ html: icon.outerHTML, className: 'marker-wrapper', iconSize: L.point(1, 1) });
                    const reversedCoordinates = toLatLngArray(coordinates)
                    mapboxMarker = L.marker(reversedCoordinates, {
                            icon: markerIcon,
                        }).setLatLng(reversedCoordinates).addTo(this.map)
                    addedMarker = mapboxMarker
                }

                mapboxMarker.on('dragstart', this.markerDragStartHandler)
                mapboxMarker.on('dragend', this.markerDragEndHandler)

                icon.poiType = poiOptions.secondaryType;
                mapboxMarker.poiType = poiOptions.secondaryType;
                if(poiOptions.subType) {
                    icon.subType = poiOptions.subType
                    mapboxMarker.subType = poiOptions.subType
                }
                
                if(poiOptions.id) {
                    icon.poiId = poiOptions.id;
                    mapboxMarker.poiId = poiOptions.id;
                    mapboxMarker.id = poiOptions.id;
                }
                if(poiOptions.text) {
                    mapboxMarker.hasText = true;
                }
                if(poiOptions.secondaryType) {
                    mapboxMarker.secondaryType = poiOptions.secondaryType;
                }
                if(poiOptions.isFavouritePoi) {
                    mapboxMarker.isFavouritePoi = true
                }
                if(poiOptions.locked) {
                    icon.locked = true
                    mapboxMarker.locked = true
                }
                if(poiOptions.discount) {
                    icon.discount = true
                    mapboxMarker.discount = true
                }
                if(dotEnabledEndpoint) {
                    if(poiOptions.text) {
                        mapboxMarker.text = poiOptions.text;
                    }
                    if(poiOptions.symbolUrl) {
                        mapboxMarker.symbolUrl = poiOptions.symbolUrl;
                    }
                }
                if(poiOptions.id && this.activePoiId === poiOptions.id) {
                    this.toggleActiveSinglePoi(mapboxMarker, true);
                }
                if(poiOptions.visibleOnZoomLevel) {
                    mapboxMarker.visibleOnZoomLevel = poiOptions.visibleOnZoomLevel
                }
                this.markers[type].push(mapboxMarker);
            }
            
            if(!this.mapGlSupported) {
                if(addedMarker !== undefined) {
                    if(icon.type) addedMarker.type = icon.type
                    if(icon.position) addedMarker.position = icon.position
                    if(icon.interimIndex) addedMarker.interimIndex = icon.interimIndex
                    if(isClickable) {
                        addedMarker.addEventListener('click', this.routeMarkerClickHandler, false)
                    } else {
                        addedMarker.addEventListener('click', this.poiClickHandler, false);
                        addedMarker.addEventListener('mouseover', this.poiMouseEnterHandler, false);
                        addedMarker.addEventListener('mouseout', this.poiMouseLeaveHandler, false);
                    }
                }
            }
        },
        removeMarker(type, secondaryType = false) {
            if (this.markers[type]) {
                if(Array.isArray(this.markers[type])) {
                    let markersArray;
                    if(secondaryType) {
                        if(secondaryType === 'favourites') {
                            markersArray = this.markers[type].filter(marker => marker.isFavouritePoi === true);
                        } else {
                            markersArray = this.markers[type].filter(marker => marker.secondaryType === secondaryType)
                        }
                    } else {
                        markersArray = this.markers[type]
                    }
                    markersArray.forEach(marker => {
                        if(this.mapGlSupported) {
                            const markerElement = marker.getElement();
                            markerElement.removeEventListener('click', this.poiClickHandler);
                            markerElement.removeEventListener('mouseenter', this.poiMouseEnterHandler);
                            markerElement.removeEventListener('mouseleave', this.poiMouseLeaveHandler);
                        } else {
                            marker.removeEventListener('click', this.poiClickHandler);
                            marker.removeEventListener('mouseover', this.poiMouseEnterHandler);
                            marker.removeEventListener('mouseout', this.poiMouseLeaveHandler);
                        }
                        marker.remove();
                    })
                    if(secondaryType) {
                        if(secondaryType === 'favourites') {
                            this.markers[type] = this.markers[type].filter(marker => marker.isFavouritePoi === undefined);
                        } else {
                            this.markers[type] = this.markers[type].filter(marker => marker.secondaryType !== secondaryType);
                        }
                    }
                } else {
                    const markerElement = this.markers[type].getElement();
                    if(type === 'singleLocation') {
                        markerElement.removeEventListener('click', this.singleLocationClickHandler);
                    }
                    markerElement.removeEventListener('mouseenter', this.draggableMarkerMouseEnterHandler);
                    markerElement.removeEventListener('mouseleave', this.draggableMarkerMouseLeaveHandler);
                    this.markers[type].off('dragstart', this.markerDragStartHandler)
                    this.markers[type].off('dragend', this.markerDragEndHandler)
                    this.markers[type].remove();
                    this.markerHovered = false
                }
                if(!secondaryType || !Object.keys(this.markers[type]).length) {
                    delete this.markers[type];
                } 
                if(this.markerLocations[type]) {
                    this.markerLocations[type].show = false
                }
            }
        },
        generatePoiMarkerContent(secondaryType, symbolUrl = false, text = false, category = false, isFavouritePoi = false) {
            let content;
            const isDot = isDotMarker(secondaryType, isFavouritePoi, text)
            if(this.dotMarkersActive && isDot/*  && category !== 'CAMERA' */) {
                content = `<div class="marker__poi-dot"></div>`;
            } else {
                if(secondaryType === 'traffic' || secondaryType === 'camera' || secondaryType === 'prediction' || category === 'CAMERA') {
                    content = `<div class="marker__poi-inner">
                            <img src="${symbolUrl}" class="marker__just-symbol" />
                        </div>`;
                } else {
                    content = `<div class="marker__poi-inner">
                        <div class="marker__poi-inner__marker">${text ? markerElements.poiMarkerSmallText : markerElements.poiMarkerSmall}</div>
                        <img src="${symbolUrl}" class="marker__poi-inner__symbol" />
                        ${text ? `<span class="marker__poi-inner__text">${text}</span>` : ''}
                    </div>`;
                }
            }
            return content;
        },
        updateLockedMarkers() {
            if(this.markers.poi) {
                this.markers.poi.forEach(marker => {
                    if(marker.locked || ONLY_LOGGED_IN_AVAILABLE.includes(marker.poiType)) {
                        const markerElement = this.mapGlSupported ? marker.getElement() : marker._icon.firstChild
                        if(this.user) {
                            markerElement.classList.remove('marker--poi--badge', 'marker--poi--lock', 'marker--poi--discount-lock')
                            if(marker.discount) markerElement.classList.add('marker--poi--badge', 'marker--poi--discount')
                        } else {
                            if(marker.discount) {
                                markerElement.classList.add('marker--poi--badge', 'marker--poi--discount-lock')
                            } else {
                                markerElement.classList.add('marker--poi--badge', 'marker--poi--lock')
                            }
                        }
                    }
                })
            }
        },
        // user location
        checkForUserLocation() {
            const permissionChecked = getPermanentData('location-permission');
            if (!permissionChecked) {
                setPermanentData('location-permission', true);
                this.getUserLocation()
                if(!this.singlePoiShown && this.centerFromUrl) {
                    this.jumpToCenter(this.centerFromUrl.center, this.centerFromUrl.zoom)
                    this.$store.commit('setCenterFromUrl', false)
                }
                setTimeout(() => {
                    this.$store.commit('setUserLocationLoaded', true)
                }, 100);
            } else {
                this.getUserLocation();
            }
        },
        getUserLocation(event = undefined) {
            const isClick = event !== undefined;
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        this.markerLocations.userLocation.coordinates = [
                            pos.coords.longitude,
                            pos.coords.latitude,
                        ];
                        this.markerLocations.userLocation.exists = true;
                        // zoom to user location
                        if(!this.singlePoiShown) {
                            // if there is a center point in the URL, jump there, else go to user location
                            if(this.centerFromUrl) {
                                this.jumpToCenter(this.centerFromUrl.center, this.centerFromUrl.zoom)
                                this.$store.commit('setCenterFromUrl', false)
                            } else if(!this.routeWaypoints.length === 0 || isClick) {
                                this.jumpToCenter([
                                    pos.coords.longitude,
                                    pos.coords.latitude,
                                ]);
                            }
                        }
                        this.addMarker('userLocation');
                        this.$store.commit(
                            'setUserLocation',
                            this.markerLocations.userLocation.coordinates,
                        );
                        this.$store.dispatch('setUserCoordinatesIfNoSingleLocation', {lat: pos.coords.latitude, lon: pos.coords.longitude})
                        this.$store.dispatch('getUserLocationAddress', {lat: pos.coords.latitude, lon: pos.coords.longitude} );
                        setTimeout(() => {
                            this.$store.commit('setUserLocationLoaded', true)
                        }, 200);
                    },
                    (error) => {
                        console.error(error);
                        if(!this.singlePoiShown && this.centerFromUrl) {
                            this.jumpToCenter(this.centerFromUrl.center, this.centerFromUrl.zoom)
                            this.$store.commit('setCenterFromUrl', false)
                        }
                        setTimeout(() => {
                            this.$store.commit('setUserLocationLoaded', true)
                        }, 100);
                        if (isClick) {
                            eventBus.$emit('showLocationPermissionMessage');
                        }
                    },
                );
            } else {
                console.error('Geolocation is not supported by this browser.');
            }
        },

        showSinglePointOnMap(waypoint, jumpToPoint = true) {
            const mapZoom = waypoint.type && waypoint.type === 'GEO_REGION' ? 8 : false
            this.markerLocations.singleLocation.coordinates = [waypoint.lon, waypoint.lat];
            this.markerLocations.singleLocation.position = waypoint.position;
            this.removeRoutesFromMap();
            if(jumpToPoint) this.jumpToCenter(this.markerLocations.singleLocation.coordinates, mapZoom);
            this.addMarker('singleLocation');
        },

        showRouteOnMap({ route, type, fitBounds = true }) {
            this.removeSinglePointFromMap();
            // if(type === 'primary') this.removeRoutesFromMap();
            let routeAlreadyExists = false
            if(this.mapGlSupported && this.map.getSource(route.id)) {
                routeAlreadyExists = true
            } else if(!this.mapGlSupported) {
                this.map.eachLayer((layer) => {
                    if(layer.id && layer.id === route.id) {
                        routeAlreadyExists = true
                    }
                })
            }

            if(routeAlreadyExists) return
            let coordinates = [];
            let source = {
                type,
                id: route.id,
                config: {
                    type: 'geojson',
                    // tolerance: 0,
                    data: {
                        type: 'FeatureCollection',
                        features: [],
                    },
                },
            };

            route.polylines.forEach((polyline) => {
                // todo: creating a separate layer for each segment of the route. maybe it's possible to merge the geometries into a single one and then just generate one layer per route
                let geometry = parse(polyline.polyline);
                coordinates = [...coordinates, ...geometry.coordinates];
                let geoJsonData = {
                    type: 'Feature',
                    geometry: geometry,
                    properties: {
                        color: polyline.lineColor, // with the filter in the layer, we can display the correct type
                        dotted: polyline.dotted ? true : false
                    },
                };
                if(polyline.markInterimStart) {
                    this.addMarker('transportChange', geometry.coordinates[0]);
                }
                if(polyline.markInterimEnd) {
                    this.addMarker('transportChange', geometry.coordinates[geometry.coordinates.length - 1]);
                }
                source.config.data.features.push(geoJsonData);
            });
            this.addRoute(source);

            if(type === 'primary') {
                // also add start and end locations
                this.markerLocations.startLocation.coordinates = [
                    this.startWaypoint.coordinate.longitude,
                    this.startWaypoint.coordinate.latitude,
                ];
                this.markerLocations.endLocation.coordinates = [
                    this.endWaypoint.coordinate.longitude,
                    this.endWaypoint.coordinate.latitude,
                ];

                // add interim stop markers to non public transport routes
                if(route.mode !== 'PUBLIC') {
                    this.addInterimMarkers();
                }

                this.addMarker('startLocation');
                this.addMarker('endLocation');

                if(fitBounds) {
                    // focus on the primary route
                    this.fitBoundsOnRoute(coordinates);
                } else {
                    this.reloadTrafficAndPOIs()
                }
            }
        },

        addInterimMarkers() {
            if(this.interimWaypoints.length) {
                this.interimWaypoints.forEach((waypoint) => {
                    let coordinates = [
                        waypoint.coordinate.longitude,
                        waypoint.coordinate.latitude,
                    ]
                    this.addMarker('interimLocation', coordinates, {}, { markerIndex: waypoint.loadedIndex });
                })
            }
        },

        addRoute(source) {
            let neighbourLayerExists = this.mapGlSupported ? this.map.getLayer(this.activeRouteId + '-shadow') : false
            const neighbour = source.type === 'secondary' && neighbourLayerExists ? this.activeRouteId + '-shadow' : this.ROUTE_LAYER_NEIGHBOUR // put secondary routes always below the shadow of the active route if it exists

            let routeFeatures = []
            if(this.mapGlSupported) {
                this.map.addSource(source.id, source.config);
            }

            // we have to stack three layers to achieve the desired effect
            // shadow behind white background
            if(this.layerStyles[source.type + 'Shadow']) {
                if(this.mapGlSupported) {
                    this.map.addLayer(
                        {
                            id: source.id + '-shadow',
                            source: source.id,
                            ...this.layerStyles[source.type + 'Shadow'],
                            filter: ['!=', 'dotted', true],
                        },
                        neighbour,
                    );
                } else {
                    const featureLayer = L.geoJson(source.config.data, {
                        style: (feature) => {
                            const styleSettings = {...this.layerStyles[source.type + 'Shadow']}
                            if(feature.properties.dotted) styleSettings.dashArray = [0, 21]
                            return styleSettings
                        },
                    })
                    routeFeatures.push(featureLayer)
                }
            }

            if(this.mapGlSupported) {
                // white background
                this.map.addLayer(
                    {
                        id: source.id + '-bg',
                        source: source.id,
                        ...this.layerStyles[source.type + 'Bg'],
                        filter: ['!=', 'dotted', true],
                    },
                    neighbour,
                );
                // color line
                this.map.addLayer(
                    {
                        id: source.id,
                        source: source.id,
                        ...this.layerStyles[source.type],
                        filter: ['!=', 'dotted', true],
                    },
                    neighbour,
                );
                // dotted layer
                this.map.addLayer(
                    {
                        id: source.id + '-dotted',
                        source: source.id,
                        ...this.layerStyles[source.type + 'Dotted'],
                        filter: ['==', 'dotted', true],
                    },
                    neighbour,
                );

            } else {
                // background
                const backgroundLayer = L.geoJson(source.config.data, {
                    style: (feature) => {
                        const styleSettings = {...this.layerStyles[source.type + 'Bg']}
                        if(feature.properties.dotted) styleSettings.dashArray = [0, 21]
                        return styleSettings
                    },
                })
                routeFeatures.push(backgroundLayer)
                // main line
                const colorLayer  = L.geoJson(source.config.data, {
                    style: (feature) => {
                        const styleSettings = {...this.layerStyles[source.type]}
                        if(source.type === 'primary') styleSettings.color = feature.properties.color
                        if(feature.properties.dotted) styleSettings.dashArray = [0, 21]
                        return styleSettings
                    },
                })
                routeFeatures.push(colorLayer)

                routeFeatures.forEach(routeFeature => {
                    routeFeature.id = source.id
                    routeFeature.addEventListener('click', (e) => {
                        const clickedRouteId = e.target && e.target.id ? e.target.id : false
                        if(clickedRouteId && clickedRouteId !== this.activeRouteId) {
                            eventBus.$emit('setRouteActiveById', clickedRouteId)
                        }
                    })
                })

                const featureGroup =  L.featureGroup(routeFeatures)
                featureGroup.addTo(this.map)
            }

            this.routes.push(source.id);

            // add hover event for secondary routes
            if(source.type === 'secondary') {
                this.addHoverListenersForRoute(source.id)
            }
        },

        addHoverListenersForRoute(layerId) {
            this.map.on('mouseenter', layerId, this.mapLayerMouseEnterHandler)
            this.map.on('mouseleave', layerId, this.mapLayerMouseLeaveHandler)
        },
        removeHoverListenersForRoute(layerId) {
            this.map.off('mouseenter', layerId, this.mapLayerMouseEnterHandler)
            this.map.off('mouseleave', layerId, this.mapLayerMouseLeaveHandler)
        },

        fitBoundsOnRoute(coordinates) {
            let bounds;
            this.mapMovedManually = true
            if(this.mapGlSupported) {
                bounds = coordinates.reduce(function (bounds, coord) {
                    return bounds.extend(coord);
                }, new this.mapbox.LngLatBounds(coordinates[0], coordinates[0]));
                let padding = this.mobile ? 50 : 100;
                if(this.mobile && this.mobileSidebarHeightMode !== 'minimal') {
                    padding = {top: 80, left: 50, right: 50, bottom: (this.mobileMapOffset * 2) + 50};
                }
                this.map.fitBounds(bounds, {
                    padding
                });
            } else {
                bounds = coordinates.reduce(function (bounds, coord) {
                    const reversedCoords = toLatLngArray(coord)
                    return bounds.extend(L.latLng(reversedCoords[0], reversedCoords[1]));
                }, L.latLngBounds(toLatLngArray(coordinates[0]), toLatLngArray(coordinates[0])));
                let padding = this.mobile ? [50, 50] : [100, 100];
                if(this.mobile && this.mobileSidebarHeightMode !== 'minimal') {
                    padding = {top: 80, left: 50, right: 50, bottom: (this.mobileMapOffset * 2) + 50};
                }

                this.map.fitBounds(bounds, {
                    padding
                });
            }

            // reload traffic messages and poisfor active route
            this.map.once('moveend', () => {
                this.reloadTrafficAndPOIs()
            });
        },
        reloadTrafficAndPOIs() {
            eventBus.$emit('loadTrafficResults');
            setTimeout(() => {
                this.mapMovedManually = false
            }, 100);

            if(this.selectedPOIs && this.selectedPOIs.length > 0) {
                this.$store.dispatch('loadPOIResults'); 
            }
        },
        removeSinglePointFromMap() {
            this.markerLocations.singleLocation.position = false
            this.removeMarker('singleLocation');
        },
        removeRoutesFromMap(routeToSkip = false) {
            if (!this.routes.length) return false;
            this.updateMapCursor('inherit')
            this.deactivateRouteMarker(this.activeRouteMarker)
            this.routes.forEach((layerId) => {
                if(this.mapGlSupported) {
                    if((!routeToSkip || routeToSkip !== layerId) && this.map.getSource(layerId)) {
                        if(this.map.getLayer(layerId + '-shadow')) this.map.removeLayer(layerId + '-shadow');
                        this.map.removeLayer(layerId + '-bg');
                        this.map.removeLayer(layerId);
                        this.map.removeLayer(layerId + '-dotted');
                        this.map.removeSource(layerId);
                        this.removeHoverListenersForRoute(layerId)
                    }
                } else {
                    this.map.eachLayer((layer) => {
                        if((!routeToSkip || routeToSkip !== layerId) && layer.id && layer.id === layerId) {
                            this.map.removeLayer(layer)
                        }
                    })
                }
            });
            if(routeToSkip) {
                this.routes = [ routeToSkip ]
            } else {
                this.routes = [];
                this.removeMarker('startLocation');
                this.removeMarker('endLocation');
                this.removeMarker('interimLocation');
                this.removeMarker('transportChange');
            }
        },
        resizeMap() {
            setTimeout(() => {
                if(this.mapGlSupported) {
                    this.map.resize();
                } else {
                    this.map.fireEvent('resize')
                }
            }, 10);
        },
        updateMapCursor(type) {
            if(!this.mapGlSupported) return // cursor already correct on leaflet
            this.map.getCanvas().style.cursor = type
        },

        // pin drop
        togglePinDropMarker(lngLat) {
            // no marker existing yet
            if (!this.markerLocations.singleLocation.show) {
                this.addPinDropMarker(lngLat);
            // pin drop already existing -> remove it and return start/end back into single location
            } else if (
                this.markerLocations.singleLocation.show &&
                this.markerLocations.singleLocation.isPinDrop
            ) {
                this.removePinDropMarker();
                const singleLocationPosition = this.markerLocations.singleLocation.position
                if(singleLocationPosition === 'start' || singleLocationPosition === 'end') {
                    this.markerLocations.singleLocation.coordinates = this.markerLocations[`${singleLocationPosition}Location`].coordinates
                    this.addMarker('singleLocation');
                    this.removeMarker(`${singleLocationPosition}Location`);
                }
            // if single location already set, turn it into start/end marker
            } else {
                const singleLocationPosition = this.markerLocations.singleLocation.position
                if(singleLocationPosition === 'start' || singleLocationPosition === 'end') {
                    this.markerLocations[`${singleLocationPosition}Location`].coordinates = this.markerLocations.singleLocation.coordinates;
                    this.addMarker(`${singleLocationPosition}Location`);
                }
                this.removeMarker('singleLocation');
                // re-add pin drop marker
                this.addPinDropMarker(lngLat);
            }
        },
        addPinDropMarker(lngLat) {
            // always deactivate route marker on mobile
            this.deactivateRouteMarker(this.activeRouteMarker)

            this.markerLocations.singleLocation.coordinates = [lngLat.lng, lngLat.lat];
            this.markerLocations.singleLocation.isPinDrop = true;
            this.addMarker('singleLocation');
            this.searchForPindropLocation(lngLat)
            this.emitDeactivatePoi();
            this.$store.dispatch('setUserCoordinatesIfNoSingleLocation', {lat: lngLat.lat, lon: lngLat.lng})
            trackEvent({ category: 'Route Planner', action: 'Pindrop', label: 'Place' })            
            trackGa4Event({ga4Parameter1Name: 'map', ga4Parameter1Value: 'pindrop_place', ga4Event: 'route_planner'}) 
        },
        removePinDropMarker() {
            if(!this.markerLocations.singleLocation.isPinDrop) return false;
            this.markerLocations.singleLocation.isPinDrop = false;
            this.removeMarker('singleLocation');
            this.removePinDropPopup();
            this.$store.dispatch('setUserCoordinatesIfNoSingleLocation', false)
            this.$store.commit('setPinDropData', false)
            eventBus.$emit('sidebarHeaderSwipeUp');
        },
        addPinDropPopup() {
            if(this.mapGlSupported) {
                this.pinDropPopup = new mapboxgl.Popup({
                    closeOnClick: false,
                    closeButton: false,
                    anchor: 'top',
                    maxWidth: '350px',
                    className: 'pin-drop-popup',
                })
                    .setLngLat(this.markerLocations.singleLocation.coordinates)
                    .setHTML(pinDropElements.loader())
                    .addTo(this.map);
            } else {
                this.pinDropPopup = L.popup({
                    closeOnClick: false,
                    closeButton: false,
                    anchor: 'top',
                    maxWidth: 350,
                    className: 'pin-drop-popup',
                    offset: L.point(0, -70)
                })
                    .setLatLng(toLatLngArray(this.markerLocations.singleLocation.coordinates))
                    .setContent(pinDropElements.loader())
                    .openOn(this.map);
            }
            
            const pinDropOffsets = pinDropElements.getPinDropOffsets(this.$refs['map-container'], this.pinDropPopup.getElement())
            this.movePinDropIntoView(pinDropOffsets)
        },
        movePinDropIntoView(offsets) {
            const panPoint = new mapboxgl.Point(0, 0)
            if(offsets.left > 0) {
                panPoint.x = -offsets.left - 10
            } else if(offsets.right > 0) {
                panPoint.x = offsets.right + 10
            }
            if(offsets.bottom > 0) {
                panPoint.y = offsets.bottom + 20
            }
            if(panPoint.x !== 0 || panPoint.y !== 0) {
                this.map.panBy(panPoint, { animate: true })
            }
        },
        async searchForPindropLocation(lngLat) {
            this.$store.commit('setPinDropLoading', true)
            if(this.markerLocations.singleLocation.isPinDrop && !this.mobile) this.addPinDropPopup()
            if(this.markerLocations.singleLocation.isPinDrop && this.mobile) eventBus.$emit('sidebarHeaderSwipeDown')
            try {
                const data = await this.searchNearby(lngLat)
                data.locationId = data.id
                data.id = `PINDROP-${lngLat.lng}-${lngLat.lat}`
                this.$store.commit('setPinDropData', data)
                this.$store.commit('setPinDropLoading', false)
                if(this.markerLocations.singleLocation.isPinDrop && !this.mobile) {
                    if(this.mapGlSupported) {
                        this.pinDropPopup.setHTML(this.generatePinDropContent())
                    } else {
                       this.pinDropPopup.setContent(this.generatePinDropContent())
                    }
                    const pinDropOffsets = pinDropElements.getPinDropOffsets(this.$refs['map-container'], this.pinDropPopup.getElement())
                    this.movePinDropIntoView(pinDropOffsets)
                }
            } catch(error) {
                console.error(error)
            }
        },
        generatePinDropContent() {
            // which buttons to show
            let buttonsHTML = pinDropElements.buttonStartGoal;
            if(this.waypointPositions.start || this.waypointPositions.end) {
                if(this.interimWaypoints.length >= 99 || this.activeTransportModeNoIntermediateStops) {
                    buttonsHTML = pinDropElements.buttonStartGoal;
                } else {
                    buttonsHTML = pinDropElements.buttonStartInterimGoal;
                }
            } else {
                buttonsHTML = pinDropElements.buttonStartGoal;
            }
            return pinDropElements.popupContent(this.pinDrop.data, buttonsHTML, this.allFavourites);
        },

        removePinDropPopup() {
            if (this.pinDropPopup) {
                this.pinDropPopup.remove();
            }
        },
        addRouteMarkerPopup(data) {
            const popupContent = pinDropElements.popupContent(data, pinDropElements.createButtonEditRemove(data.waypointIndex, this.routeWaypoints.length > 2), false, 'routepopup')
            if(this.mapGlSupported) {
                this.routeMarkerPopup = new mapboxgl.Popup({
                    closeOnClick: false,
                    closeButton: false,
                    anchor: 'top',
                    maxWidth: '350px',
                    className: 'pin-drop-popup',
                })
                    .setLngLat(data.coordinates)
                    .setHTML(popupContent)
                    .addTo(this.map);
            } else {
                this.routeMarkerPopup = L.popup({
                    closeOnClick: false,
                    closeButton: false,
                    anchor: 'top',
                    maxWidth: 350,
                    className: 'pin-drop-popup',
                    offset: L.point(0, -70)
                })
                    .setLatLng(toLatLngArray(data.coordinates))
                    .setContent(popupContent)
                    .openOn(this.map);
            }
            
            const pinDropOffsets = pinDropElements.getPinDropOffsets(this.$refs['map-container'], this.routeMarkerPopup.getElement())
            this.movePinDropIntoView(pinDropOffsets)
        },
        removeRouteMarkerPopup() {
            if (this.routeMarkerPopup) {
                this.routeMarkerPopup.remove();
            }
        },
        async searchNearby(lngLat) {
            try {
                const response = await api.location.getClosestLocation(lngLat.lng, lngLat.lat)
                let data = {}
                if(response.data.data) {
                    data = response.data.data[0]
                    data.realLocation = true
                    data.label = `${data.name}, ${data.comment}`
                } else {
                    data = {
                        name: 'Markierung',
                        lon: lngLat.lng,
                        lat: lngLat.lat,
                        realLocation: false,
                        label: `${lngLat.lat}, ${lngLat.lng}`
                    }
                }
                return data
            } catch(error) {
                console.error(error)
            }
        },

        async updateRouteWithDrag(lngLat, markerPosition = 'start', markerPositionIndex = false, insertAfterIndex = false) {
            const nearbyLocation = await this.searchNearby(lngLat)
            eventBus.$emit('addLocationToWaypoints', { 
                waypoint: nearbyLocation,
                position: markerPosition,
                positionIndex: markerPositionIndex, // only for 'between' position
                insertAfterIndex: insertAfterIndex, // only for between and drag route markers
                disableLocationJump: true,
            })
        },

        updateStyle() {
            this.previousRoutes = [...this.routes]
            this.previousTrafficLines = [...this.trafficLines]
            if(this.map?.setStyle) this.map.setStyle(this.mapStyle);
        },
        storeMapBounds() {
            const mapBounds = this.map.getBounds()
            this.$store.commit('setMapBounds', mapBounds)
        },

        // POI
        removePoiResults(poiType, isSubApi = false) {
            this.removeMarker('poi', poiType);
            this.removeAreasOfType(poiType);
            if(poiType === 'traffic') this.removeTrafficLinesFromMap()
            // check whether poiType has subtypes and if those are active, remove areas accordingly
            const checkApiType = isSubApi ? getParentApi(poiType) : poiType;
            const subApiTypes = getSubApiTypes(checkApiType)
            if(subApiTypes) {
                const poiParams = getPoiParams(checkApiType)
                const activeSubApiTypes = getActiveSubApiTypes(false, poiParams, checkApiType)
                subApiTypes.forEach(subType => {
                    if(!activeSubApiTypes || !activeSubApiTypes.includes(subType)) {
                        this.removeMarker('poi', subType);
                        this.removeAreasOfType(subType);
                    }
                })
            } 
        },
        showPoiResults(results, poiType, isSubApi) {
            if(results) {
                if(this.AREA_POI_TYPES.includes(poiType)) {
                    this.showPoiResultsArea(results, poiType, isSubApi);
                }
                if(this.SKIP_PINS_ON_MAP.includes(poiType)) return;

                if(poiType === 'traffic' || poiType === 'camera' || poiType === 'prediction') {
                        results.forEach(result => {
                            this.showPoiResultsOfTrafficType(result, poiType);
                        });
                } else {
                    this.showPoiResultsOfGeneralType(results, poiType, isSubApi);
                }
            }
        },
        showPoiResultsOfGeneralType(results, poiType, isSubApi) {
            results.forEach(result => {
                const id = getPoiId(poiType, result);
                const type = getPoiType(poiType, result)

                const coordinatesObject = getCoordinatesObject(poiType, result);
                let coordinates = [
                    coordinatesObject.longitude,
                    coordinatesObject.latitude,
                ];
                let price = false;
                const priceUnformated = getPOIPrice(result, type);
                if(priceUnformated){
                    let decimals = type === 'gasStations' ? 3 : 2;
                    price = priceUnformated ? formatPrice(priceUnformated, false, decimals) : false;
                }
                let category = getCategory(poiType, result);
                const symbolUrl = getImageForCategory(poiType, category, 'symbol', result);
                const discount = isOeamtcPartner(poiType, result);
                const star = isFavourite(id)
                const isFavouritePoi = poiType === 'favourites'
                let subType = isSubApi ? poiType : false
                if(isFavouritePoi) {
                    if(type === 'partners' || type === 'traffic') subType = getSubType(poiType, result)
                }
                const locked = !this.user && ONLY_LOGGED_IN_AVAILABLE.includes(type)
                const poiMarkerOptions = {
                    secondaryType: type, 
                    symbolUrl, 
                    text: price, 
                    id: id,
                    category,
                    discount,
                    star,
                    subType: subType,
                    isFavouritePoi,
                    locked
                }
                this.addMarker('poi', coordinates, poiMarkerOptions);
            })
        },
        showPoiResultsOfTrafficType(trafficItem, poiType) {
            // camera
            if (trafficItem.data && trafficItem.data.geoData) {
                const category = 'CAMERA'
                let coordinates = [
                    trafficItem.data.geoData.coordinates[0].longitude,
                    trafficItem.data.geoData.coordinates[0].latitude
                ]
                const symbolUrl = getImageForCategory('traffic', category, 'symbol');
                const id = getPoiId('camera', trafficItem)
                const star = isFavourite(id)
                const poiMarkerOptions = {
                    subType: 'camera',
                    secondaryType: 'camera', 
                    symbolUrl, 
                    id: id,
                    category: category,
                    star: star,
                }
                this.addMarker('poi', coordinates, poiMarkerOptions);
            // traffic item with multiple markers
            } else if(trafficItem.location) {
                if(trafficItem.location.line) {
                    this.addTrafficLine(trafficItem)
                }
                const trafficPointsAmount = trafficItem.location.points.length
                trafficItem.location.points.forEach((result, index) => {
                    let coordinates = [
                        result.longitude,
                        result.latitude,
                    ];
                    const symbolUrl = getImageForCategory('traffic', trafficItem.categorization.type, 'symbol');
                    const poiMarkerOptions = {
                        secondaryType: 'traffic',
                        symbolUrl, 
                        id: getPoiId('traffic', trafficItem),
                        category: trafficItem.categorization.type,
                        hiddenOnLowerZoomlevel: index >= 1,
                        hiddenOnMediumZoomlevel: index > 0 && (index < trafficPointsAmount - 1),
                        visibleOnZoomLevel: getZoomLevelVisible('traffic', trafficItem)
                    }
                    if(poiType !== 'traffic') poiMarkerOptions.subType = poiType
                    this.addMarker('poi', coordinates, poiMarkerOptions);
                    
                })
            // traffic item with one marker
            } else {
                let coordinates = [
                    trafficItem.longitude,
                    trafficItem.latitude,
                ];
                const symbolUrl = getImageForCategory('traffic', trafficItem.type, 'symbol');
                const poiMarkerOptions = {
                    secondaryType: poiType, 
                    symbolUrl, 
                    id: getPoiId('traffic', trafficItem),
                    category: trafficItem.type,
                    visibleOnZoomLevel: getZoomLevelVisible('traffic', trafficItem)
                }
                this.addMarker('poi', coordinates, poiMarkerOptions);
            }
        },
        showPoiResultsArea(results, poiType, isSubApi) {
            // results.forEach(result => {
            //     this.drawArea(result, poiType, isSubApi);
            // });
            this.drawAreas(results, poiType, isSubApi)
        },
        emitDeactivatePoi() {
            this.$store.commit('setActivePoiId', false)
        },
        emitActivatePoi(id) {
            this.$store.commit('setActivePoiId', id)
        },
        emitToggleHighlightPoi(id, highlight) {
            eventBus.$emit('toggleHighlightPoi', {id, highlight});
        },
        toggleActivePoi(id, activate = true) {
            const markers = this.getPoiMarkersById(id);
            const areaType = this.getPoiAreaTypeById(id);
            if(areaType) {
                if(activate) {
                    this.highlightArea(areaType, id, 'active')
                } else {
                    this.dehighlightArea(areaType, id, 'active')
                }
            }
            if(this.activePoiAreaDisplayed) {
                this.removeAreasOfType(this.activePoiAreaDisplayed)
                this.activePoiAreaDisplayed = false
            }
            if(!markers || !markers.length) return false;
            markers.forEach(marker => {
                this.toggleActiveSinglePoi(marker, activate);
            })
            let markerCoordinates
            if(this.mapGlSupported) {
                markerCoordinates = markers[0].getLngLat()
            } else {
                markerCoordinates = markers[0].getLatLng()
            }
            const markerCoordinatesInsideBounds = this.mapBounds.contains(markerCoordinates)
            const isDot = isDotMarker(markers[0].secondaryType, false, markers[0].hasText)
            if(activate && ((this.dotMarkersActive && isDot) || !markerCoordinatesInsideBounds)) {
                this.jumpToPoi(markerCoordinates);
            }
        },
        toggleActiveSinglePoi(marker, activate = true) {
            const markerElement = marker.getElement();
            const innerMarkerElement = this.mapGlSupported ? markerElement :  markerElement.querySelector('.marker')
            if(activate) {
                this.removePinDropMarker();
                innerMarkerElement.classList.add('is-marker-active');
            } else {
                innerMarkerElement.classList.remove('is-marker-active');
            }
            marker.isActive = activate;
            if(marker.isHighlight) return false;
            this.changePoiMarkerIcon(activate, markerElement, marker.hasText);
        },
        toggleHighlightPoi(data) {
            const markers = this.getPoiMarkersById(data.id);
            const areaType = this.getPoiAreaTypeById(data.id);
            if(areaType) {
                if(data.highlight) {
                    this.highlightArea(areaType, data.id, 'hover')
                } else {
                    this.dehighlightArea(areaType, data.id, 'hover')
                }
            }
            if(markers && markers.length) {
                markers.forEach(marker => {
                    if(data.highlight && marker.isHighlight) return false;
                    const markerElement = marker.getElement();
                    const innerMarkerElement = this.mapGlSupported ? markerElement :  markerElement.querySelector('.marker')
                    if(data.highlight) {
                        innerMarkerElement.classList.add('is-marker-highlight');
                    } else {
                        innerMarkerElement.classList.remove('is-marker-highlight');
                    }
                    markerElement.highlight = data.highlight;
                    marker.isHighlight = data.highlight;
                    if(marker.isActive) return false;
                    // this.toggleSingleLocationMarkerSize(!data.highlight);
                    this.changePoiMarkerIcon(data.highlight, markerElement, marker.hasText);
                })
            }
            return false
        },
        changePoiMarkerIcon(big = true, markerElement, hasText = false) {
            let icon;
            const markerEl = markerElement.querySelector('.marker__poi-inner__marker');
            if(!markerEl) return false;
            if(big) {
                icon = hasText ? markerElements.poiMarkerBigText : markerElements.poiMarkerBig
            } else {
                icon = hasText ? markerElements.poiMarkerSmallText : markerElements.poiMarkerSmall
            }
            markerEl.innerHTML = icon;
        },
        changeRouteMarkerIcon(big = true, markerElement, interimIndex = false) {
            let icon;
            const markerEl = markerElement;
            if(!markerEl) return false;
            if(big) {
                icon = `<div class="marker__default-highlight">
                            ${markerElements.poiMarkerBig}
                            ${interimIndex ? `<div class="marker__default-highlight__number">${interimIndex}</div>` : '<div class="marker__default-inner marker__default-inner--highlight"></div>'}
                        </div>`
            } else {
                icon = `<div class="marker__default-inner">${interimIndex || ''}</div>`
            }
            markerEl.innerHTML = icon
        },
        getPoiMarkersById(id) {
            if(!this.markers.poi) return false;
            return this.markers.poi.filter(marker => marker.id === id);
        },
        getMarkersByType(type) {
            if(!this.markers[type]) return false
            return this.markers[type]
        },
        getPoiAreaTypeById(id) {
            const subAreas = Object.keys(this.areas)
            if(subAreas && subAreas.length) {
                for (const subArea of subAreas){
                    if(this.areas[subArea].includes(id)) {
                        return subArea
                    }
                }
            }
            return false
        },
        activatePoi(id, poiType, poiSubType) {
            this.mapPOI = true;
            this.emitActivatePoi(id);
            this.$store.commit('setCustomActivePoiType', poiType);
            const subType = poiSubType ?? false
            this.$store.commit('setCustomActivePoiSubType', subType);
        },
        activateRouteMarker(type, interimIndex = false) {
            // deactivate the pindrop
            this.removePinDropMarker()

            this.deactivateRouteMarker(this.activeRouteMarker)
            let marker = this.getMarkersByType(type)
            if(interimIndex) marker = marker[interimIndex - 1]
            const markerElement = this.mapGlSupported ? marker.getElement() : marker._icon.firstChild
            this.changeRouteMarkerIcon(true, markerElement, interimIndex)
            this.activeRouteMarker = { type: type, index: interimIndex }
            // determine index to get data for popup from routeWaypoints
            let waypointIndex = 0
            if(type === 'endLocation') waypointIndex = this.routeWaypoints.length - 1
            if(type === 'interimLocation') waypointIndex = interimIndex
            const waypointData = this.routeWaypoints[waypointIndex]
            const popupData = {
                waypointIndex: waypointIndex,
                coordinates: [waypointData.coordinate.longitude, waypointData.coordinate.latitude],
                lat: waypointData.coordinate.latitude,
                lon: waypointData.coordinate.longitude,
                name: waypointData.name,
                comment: waypointData.comment,
                realLocation: waypointData.realLocation,
                label: waypointData.label,
                type: 'routemarker',
            }
            this.$store.commit('setPinDropData', popupData)
            if(this.mobile) {
                eventBus.$emit('sidebarHeaderSwipeDown')
            } else {
                this.addRouteMarkerPopup(popupData)
            }
        },
        deactivateRouteMarker(markerData) {
            if(!markerData) return
            let marker = this.getMarkersByType(markerData.type)
            if(markerData.index) marker = marker[markerData.index - 1]
            const markerElement = this.mapGlSupported ? marker.getElement() : marker._icon.firstChild
            this.changeRouteMarkerIcon(false, markerElement, markerData.index)
            this.activeRouteMarker = false
            this.removeRouteMarkerPopup()
        },
        showPoiReloadButton() {
            if(this.selectedPOIs.length) {
                this.poiReloadButtonVisible = true;
            }
        },
        reloadPois() {
            eventBus.$emit('scrollToPOIPicker');
            this.$store.dispatch('loadPOIResults');
        },
        isZoomIn() {
            const currentMapBounds = this.map.getBounds()
            const previousMapBound = this.mapBounds;
            let northEastKey = '_ne'
            let southWestKey = '_sw'
            if(!this.mapGlSupported) {
                northEastKey = '_northEast'
                southWestKey = '_southWest'
            }
            return  currentMapBounds[northEastKey].lng < previousMapBound[northEastKey].lng &&
                    currentMapBounds[northEastKey].lat < previousMapBound[northEastKey].lat &&
                    currentMapBounds[southWestKey].lng > previousMapBound[southWestKey].lng &&
                    currentMapBounds[southWestKey].lat > previousMapBound[southWestKey].lat
        },
        reloadPoisAfterDelay() {
            this.mapOnlyZoomedIn = true;
            if(this.automaticPoiReloadTimeout) {
                clearTimeout(this.automaticPoiReloadTimeout);
            }
            this.automaticPoiReloadTimeout = setTimeout(() => {
                if(this.mapOnlyZoomedIn) {
                    this.reloadPois();
                    this.mapOnlyZoomedIn = false;
                    this.automaticPoiReloadTimeout = false;
                }
            }, this.AUTOMATIC_POI_RELOAD_DELAY);
        },
        updateBottomElPlacement() {
            if(!this.mobile) return false;
            const copyrightEl = this.$el.querySelector('.mapboxgl-ctrl-bottom-right')
            const correctionMultiplier = this.mobileSidebarHeightMode === 'middle' ? 0.5 : 1;
            this.bottomElOffset = `calc(${this.mobileSidebarHeight} - ${49 * correctionMultiplier}px`;
            copyrightEl.style.bottom = this.bottomElOffset;
        },
        storeZoom() {
            this.mapZoomLevel = this.map.getZoom();
            this.$store.commit('setMapZoom', this.mapZoomLevel)
        },
        toggleDotPoiMarkers() {
            if(!this.markers.poi) return false;
            if(!this.selectedPOIs.some(poiType => hasDotMarkerType(poiType))) return false;
            let dotEnabledMarkers = this.markers.poi.filter(marker => isDotMarker(marker.secondaryType, false, marker.hasText));
            if(!dotEnabledMarkers.length) return false;
            dotEnabledMarkers.forEach(marker => {
                let markerElement = marker.getElement();
                if(markerElement.classList.contains('marker-wrapper')) {
                    markerElement = markerElement.querySelector('.marker')
                }
                markerElement.innerHTML = this.generatePoiMarkerContent(marker.secondaryType, marker.symbolUrl, marker.text);
                if(marker.isActive) {
                    this.changePoiMarkerIcon(true, markerElement, marker.hasText);
                }
            })
        },
        toggleZoomLevelBasedMarkers() {
            if(!this.markers.poi) return false;
            if(!this.selectedPOIs.some(poiType => isZoomLevelBasedType(poiType))) return false;
            const zoomEnabledMarkers = this.markers.poi.filter(marker => isZoomLevelBasedType(marker.poiType))
            zoomEnabledMarkers.forEach(marker => {
                if(marker.visibleOnZoomLevel) {
                    let markerElement = marker.getElement();
                    if(markerElement.classList.contains('marker-wrapper')) {
                        markerElement = markerElement.querySelector('.marker')
                    }
                    if(this.mapZoomLevel < marker.visibleOnZoomLevel) {
                        markerElement.classList.add('is-marker-hidden')
                    } else {
                        markerElement.classList.remove('is-marker-hidden')
                    }
                }
            })
        },
        checkClickedFeatures(e) {
            if(!this.mapGlSupported) return
            const routeBbox = [[e.point.x - 5, e.point.y - 5], [e.point.x + 5, e.point.y + 5]];
            const areaBbox = [[e.point.x, e.point.y], [e.point.x, e.point.y]];
            const routeFeatures = this.map.queryRenderedFeatures(routeBbox, { layers: this.routes });
            let areas = []
            for (const area in this.areas) {
                if(this.areas[area].length > 0) {
                    areas.push(area)
                }
            }
            areas = areas.map(area => `area-fill-${area}`)
            const areaFeatures = this.map.queryRenderedFeatures(areaBbox, { layers: areas })
            if(routeFeatures.length > 0) {
                const clickedRouteId = routeFeatures[0].layer ? routeFeatures[0].layer.id : false
                if(clickedRouteId && clickedRouteId !== this.activeRouteId) {
                    eventBus.$emit('setRouteActiveById', clickedRouteId)
                }
            }
            if(areaFeatures.length > 0) {
                if(areaFeatures.length && areaFeatures[0].layer && areaFeatures[0].id) {
                    const poiData = areaFeatures[0].properties
                    this.activatePoi(poiData.poiId, poiData.type, poiData.subType)
                }
            }
        },
        checkMouseDownFeatures(e) {
            if(!this.mapGlSupported) return
            const routeBbox = [[e.point.x - 5, e.point.y - 5], [e.point.x + 5, e.point.y + 5]];
            const allRoutes = [...this.routes, ...this.routes.map(r => r + '-dotted')]
            const routeFeatures = this.map.queryRenderedFeatures(routeBbox, { layers: allRoutes });
            if(routeFeatures.length > 0) {
                const clickedRouteId = routeFeatures[0].layer ? routeFeatures[0].layer.id.replace('-dotted', '') : false
                if(clickedRouteId && clickedRouteId === this.activeRouteId) {
                    this.dragRouteMarkerVisible = true
                    let closestSectionIndex = 0
                    let smallestDistance
                    this.activeRouteSectionPolylines.forEach((section, index) => {
                        const coords = parse(section.polylineWKT)
                        const distance = turfPointToLineDistance(e.lngLat.toArray(), coords, {units: 'kilometers'})
                        if(!smallestDistance || distance < smallestDistance) {
                            smallestDistance = distance
                            closestSectionIndex = index
                        }
                    })
                    if(this.markers.dragRoute) {
                        this.dragRouteMarkerInsertAfter = closestSectionIndex
                    }
                }
            }
        },
        toggleSingleLocationActivity(activate) {
            if(this.markerLocations.singleLocation.show) {
                this.toggleActiveSinglePoi(this.markers['singleLocation'], activate);
            }

        },
        drawAreas(results, poiType, isSubApi) {
            const displaySettings = getAreaDisplaySettings(poiType, this.mapGlSupported)
            const areaFeatureCollection = []
            if(!this.areas[poiType]) {
                this.areas[poiType] = [];
            }
            results.forEach(result => {
                const id = getPoiId(poiType, result)
                const areas = getAreas(result);
                let subCategory = result.data && result.data.header && result.data.header.categories ? result.data.header.categories[0] : 'default'
                areas.forEach((area) => {
                    const parsedArea = parse(area.area);
                    let level = false

                    if(poiType === 'travelRadar') {
                        subCategory = result.header.level
                        if(areas.length > 1  && area.level) {
                           subCategory = false
                           level = area.level
                        }
                    }

                    const feature = {
                        'type': 'Feature',
                        'geometry': parsedArea,
                        'properties': {
                            'type': poiType,
                            'poiId': id,
                            'subType': isSubApi ? poiType : false,
                            'subCategory': subCategory,
                            'level': level, // level color for multiple areas (travel radar)
                            'area': turfArea(parsedArea)
                        },
                    }
                    this.areas[poiType].push(id);
                    areaFeatureCollection.push(feature)
                })
            })

            if(this.mapGlSupported) {
                this.map.addSource(`area-${poiType}`, {
                    'type': 'geojson',
                    'promoteId': 'poiId',
                    'data': {
                        'type': 'FeatureCollection',
                        'features': areaFeatureCollection
                    },
                });

                if(!this.map.getLayer(`area-fill-${poiType}`)) {
                    this.map.addLayer({
                        'id': `area-fill-${poiType}`,
                        'type': 'fill',
                        'source': `area-${poiType}`,
                        'layout': {
                            'fill-sort-key': ['-', ['get', 'area']]
                        },
                        'paint': {
                            'fill-color': displaySettings.color, 
                            'fill-opacity': [
                                'case',
                                ['boolean', ['feature-state', 'hover'], false],
                                ...displaySettings.fillOpacity
                            ]
                        },
                    }, this.AREA_LAYER_NEIGHBOUR);


                    this.addHoverListenersForArea(`area-fill-${poiType}`)
                }

                if(!this.map.getLayer(`area-outline-${poiType}`)) {
                    this.map.addLayer({
                        'id': `area-outline-${poiType}`,
                        'type': 'line',
                        'source': `area-${poiType}`,
                        'paint': {
                            'line-color': displaySettings.color,
                            'line-width': [
                                'case',
                                ['boolean', ['feature-state', 'hover'], false],
                                2,
                                1
                            ],
                            'line-opacity': [
                                'case',
                                ['boolean', ['feature-state', 'hover'], false],
                                ...displaySettings.lineOpacity
                            ]
                        }
                    }, this.AREA_LAYER_NEIGHBOUR);
                }
            } else {
                const polygonLayer = L.geoJson(areaFeatureCollection, {
                    style: (feature) => {
                        const styleSettings = {
                            stroke: true,
                            color: displaySettings.color,
                            opacity: displaySettings.lineOpacity.default,
                            weight: 1,
                            fill: true,
                            fillColor: displaySettings.color,
                            fillOpacity: displaySettings.fillOpacity.default,
                        }
                        if(displaySettings.categories && feature.properties.subCategory && displaySettings.categories[feature.properties.subCategory]) {
                            styleSettings.opacity = displaySettings.categories[feature.properties.subCategory].lineOpacity.default
                            styleSettings.fillOpacity  = displaySettings.categories[feature.properties.subCategory].fillOpacity.default
                        }
                        return styleSettings
                    },
                    onEachFeature: (feature, layer) => {
                        let lineOpacitySettings = displaySettings.lineOpacity
                        let fillOpacitySettings = displaySettings.fillOpacity
                        if(displaySettings.categories && feature.properties.subCategory && displaySettings.categories[feature.properties.subCategory]) {
                            lineOpacitySettings = displaySettings.categories[feature.properties.subCategory].lineOpacity
                            fillOpacitySettings  = displaySettings.categories[feature.properties.subCategory].fillOpacity
                        }
                        layer.on('mouseover', function () {
                            this.setStyle({
                                opacity: lineOpacitySettings.hover,
                                fillOpacity: fillOpacitySettings.hover,
                            });
                        });
                        layer.on('mouseout', function () {
                            this.setStyle({
                                opacity: lineOpacitySettings.default,
                                fillOpacity: fillOpacitySettings.default,
                            });
                        });
                        layer.on('click', () => {
                            this.activatePoi(feature.properties.poiId, feature.properties.type, feature.properties.subType)
                        });
                    }
                })
                polygonLayer.type = poiType
                polygonLayer.addTo(this.map)
            }
        },
        removeAreasOfType(type) {
            if(!this.areas[type] || !this.areas[type].length) return;

            if(this.mapGlSupported) {
                this.map.removeLayer(`area-fill-${type}`)
                this.map.removeLayer(`area-outline-${type}`)
                this.map.removeSource(`area-${type}`)
                this.removeHoverListenersForArea(`area-fill-${type}`)
            } else {
                this.map.eachLayer((layer) => {
                    if(layer.type && layer.type === type) {
                        this.map.removeLayer(layer)
                    }
                })
            }

            this.areas[type] = [];
        },
        addHoverListenersForArea(layerId) {
            this.map.on('mousemove', layerId, this.mapAreaMouseMoveHandler)
            this.map.on('mouseleave', layerId, this.mapAreaMouseLeaveHandler)
        },
        removeHoverListenersForArea(layerId) {
            this.map.off('mousemove', layerId, this.mapAreaMouseMoveHandler)
            this.map.off('mouseleave', layerId, this.mapAreaMouseLeaveHandler)
        },
        highlightArea(poiType, poiId, type = 'hover') {
            if(!this.mapGlSupported) return
            const area = {
                id: poiId,
                sourceId: `area-${poiType}`,
            }
            if(!this.map.getSource(area.sourceId)) return
            this.map.setFeatureState(
                { source: area.sourceId, id: area.id },
                { hover: true }
            );
            if(type === 'hover') {
                this.hoveredAreaId = area.id
            } else {
                this.highlightAreaId = area.id
            }
        },
        dehighlightArea(poiType, poiId, type = 'hover') {
            if(!this.mapGlSupported) return
            const area = {
                id: poiId,
                sourceId: `area-${poiType}`,
            }
            if(!this.map.getSource(area.sourceId)) return
            if(type === 'hover' && this.hoveredAreaId === this.highlightAreaId) {
                this.hoveredAreaId = null
                return
            }
            this.map.setFeatureState(
                { source: area.sourceId, id: area.id },
                { hover: false }
            );
            if(type === 'hover') {
                this.hoveredAreaId = null
            } else {
                this.highlightAreaId = null
            }
        },
        showPreviousRoutes() {
            this.previousRoutes.forEach(route => {
                let formattedRoute = getRouteById(route)
                formattedRoute = getListResultRouteData(formattedRoute)
                let type = 'secondary'
                if(route === this.activeRouteId) {
                   type = 'primary' 
                }
                eventBus.$emit('showRouteOnMap', { route: formattedRoute, type: type, fitBounds: false });
            })
            this.previousRoutes = []
        },
        addTrafficLine(trafficItem) {
            const source = {
                type: 'traffic',
                id: trafficItem.header.id,
                config: {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [{
                            type: 'Feature',
                            geometry:  parse(trafficItem.location.line),
                        }],
                    },
                },
            }
            if(this.mapGlSupported) {
                this.map.addSource(source.id, source.config);
                this.map.addLayer(
                    {
                        id: source.id,
                        source: source.id,
                        ...this.layerStyles[source.type],
                        minzoom: this.TRAFFIC_MARKER_ZOOM_BREAKPOINT,

                    },
                );
            } else {
                let routeFeatures = []
                const lineLayer  = L.geoJson(source.config.data, {
                    style: () => {
                        const styleSettings = {...this.layerStyles[source.type]}
                        return styleSettings
                    },
                })
                lineLayer.id = source.id
                routeFeatures.push(lineLayer)
                const featureGroup =  L.featureGroup(routeFeatures)
                featureGroup.addTo(this.map)
            }

            this.trafficLines.push({
                id: source.id,
                item: {
                    header: {
                        id: trafficItem.header.id
                    },
                    location: {
                        line: trafficItem.location.line
                    }
                }
            });
        },
        removeTrafficLinesFromMap() {
            if (!this.trafficLines.length) return false;
            this.trafficLines.forEach(trafficLine => {
                if(this.mapGlSupported) {
                    if(this.map.getSource(trafficLine.id)) {
                        this.map.removeLayer(trafficLine.id);
                        this.map.removeSource(trafficLine.id);
                    }
                } else {
                    this.map.eachLayer((layer) => {
                        if(layer.id && layer.id === trafficLine.id) {
                            this.map.removeLayer(layer)
                        }
                    })
                }
            })
            this.trafficLines = []
        },
        showPreviousTrafficLines() {
            this.previousTrafficLines.forEach(trafficLine => {
                this.addTrafficLine(trafficLine.item)
            })
            this.previousTrafficLines = []
        },
        // URL
        urlQueryCenterDifferent(route) {
            const queryData = decodeQueryParams(route.query)
            const currentCenter = this.map.getCenter()
            if(!queryData.center) return true
            if(currentCenter.lat !== queryData.center.lat && currentCenter.lng !== queryData.center.lng) {
                return true
            }
            return false
        },
        updateUrlQuery: debounce(function() {
            if(this.urlQueryCenterDifferent(this.$route)) {
                updateQueryParams(this.$route, {
                    center: this.map.getCenter(),
                    zoom: this.map.getZoom()
                })
            }
        }, 500),
        checkToLoadAdditionalPOIs: debounce(async function() {
            if(this.mapZoomLevel >= 11) {

                if(this.selectedPOIs.includes('carParks')) {
                    const mainType = 'carParks'
                    const subType = 'shorttermparking'
                    const poiParams = getPoiParams(mainType)
                    if(poiParams.category.values.includes('SHORTTERMPARKING') && (!this.areas.shorttermparking || this.areas.shorttermparking.length === 0) && !this.loadingPOIs.includes(mainType)) {
                        // only load shorttermparking map entries
                        this.$store.commit('setLoadingPOIType', {type: mainType, loading: true});
                        const params  = getPoiParams(mainType, true)
                        const responseMap = await api.poi.getPOIsForMapByType(subType, params);
                        let dataMap = getPOIResultByType(responseMap, subType);
                        const moreResponseMap = await loadMorePOIResultsWithPagination(subType, params, mainType, responseMap)
                        dataMap = dataMap || []
                        dataMap = [...dataMap, ...moreResponseMap]
                        eventBus.$emit('removePoisFromMap', {type: subType})
                        eventBus.$emit('showPoisOnMap', {type: subType, data: dataMap, isSubApi: true})
                        // also reload the list
                        const responseList = await api.poi.getPOIsForListByType('parking', {...poiParams});
                        const dataList = getPOIResultByType(responseList, mainType);
                        eventBus.$emit('scrollToPOIPicker');
                        this.$store.commit('setPoiResultsByType', {type: mainType, data: dataList});
                        if(responseList.data && responseList.data.header && responseList.data.header.count !== undefined) {
                            this.$store.commit('setPOIPaginationDataByType', {
                                type: mainType,
                                more: responseList.data.header.count < responseList.data.header.totalCount,
                                userCoordinates: this.userCoordinates ? this.userCoordinates : false,
                                apiType: 'parking',
                                params: poiParams
                            })
                        }
                        this.$store.commit('setLoadingPOIType', {type: mainType, loading: false});
                    }
                }
            }
        }, 500)
    },
};
</script>

<style lang="scss">
.map {
    $block: &;
    width: 100%;
    height: 100%;
    @include mq($from: tablet) {
        &.is-tablet-sidebar-toggled {
            position: absolute;
            right: 0;
            width: calc(100% + #{$sidebar-width});
        }
    }

    &.is-on-low-zoomlevel {
        .traffic-line {
            display: none;
        }
    }
    
    .mgl-map-wrapper {
        width: 100%;
        height: 100%;
    }
    .mapboxgl-map {
        width: 100%;
        height: 100%;
    }

    &__container {
        width: 100%;
        height: 100%;
    }

    // map canvas
    .mapboxgl-canvas {
        &:focus,
        &:active {
            outline: none;
        }
    }

    .leaflet-container {
        .blurred-path {
            filter: url(#path-dropshadow)
        }
    }

    .mapboxgl-popup {
        z-index: $marker-highest-z + 5;
    }

    &__controls {
        position: absolute;
        z-index: $marker-highest-z + 10;

        @include mq($until: tablet) {
            display: flex;
        }

        &--top-left {
            top: 30px;
            left: 30px;

            @include mq($until: tablet) {
                top: 16px;
                left: 16px;
            }
        }

        &--top-right {
            top: 30px;
            right: 30px;

            @include mq($until: tablet) {
                top: 16px;
                right: 16px;
            }

            @include mq($from: tablet, $and: '(max-height: 370px)') {
                top: 10px;
            }
        }

        &--bottom-right {
            bottom: 30px;
            right: 30px;

            @include mq($from: tablet, $and: '(max-height: 370px)') {
                bottom: 25px;
            }
        }

        &__group {
            border-radius: 10px;
            overflow: hidden;
            box-shadow: $elevation-shadow;

            @include mq($until: tablet) {
                display: flex;
                justify-content: flex-end;
            }

            &:not(:last-child) {
                margin-bottom: 16px;

                @include mq($until: tablet) {
                    margin-bottom: 0;
                    margin-left: 12px;
                }

                @include mq($from: tablet, $and: '(max-height: 370px)') {
                    margin-bottom: 8px;
                }
            }
        }

        &__item {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 42px;
            height: 42px;
            background: $color-yellow;
            transition: background 0.2s $ease-out-quint;
            cursor: pointer;

            @include mq($until: tablet) {
                width: 42px;
                height: 42px;
            }

            &--locate {
                @include mq($until: tablet) {
                    order: 2;
                }
            }

            .no-touch &:hover {
                background-color: $color-yellow-dark;
            }
        }
    }
    &__poi-reload {
        position: absolute;
        z-index: $marker-highest-z + 10;
        left: 50%;
        transform: translateX(-50%);
        box-shadow: $elevation-shadow;
        margin-bottom: 16px;
        @include mq(tablet) {
            top: 30px;
        }
    }
    &__screen-bottom-el,
    .mapboxgl-ctrl-bottom-right {
        @include mq($until: tablet) {
            transition: opacity 0.2s;
            bottom: calc(50% - 24px);
            .is-sidebar-moving-transition &,
            .is-sidebar-moving-touch & {
                transition-delay: 0s;
                opacity: 0;
            }
        }
    }
    .mapboxgl-ctrl-bottom-right {
        z-index: $marker-highest-z + 11;
    }

    &__placeholder-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .mapboxgl-ctrl-top-left {
        .mapboxgl-ctrl-scale {
            @include mq($until: tablet) {
                margin-top: 27px;
                margin-left: 75px;
            }
        }
    }
}
</style>
